import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Zap, ArrowLeft, Brain, Package } from 'lucide-react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { getAppVersion, getIssueTypes, getProcessModelTitle, getModelMetrics, getModelMetricsTotal, getModelID, taskJSON, getFieldUsage, getStatusTypeSummary, getStatusTypeMappings, getAttributePrivacyRules, getActivityType, getActivityContext } from '@process-advisor/process-miner/processMiner/processMiner';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import JMWE_Icon from '@process-advisor/process-miner/dashboard/JMWE.png';
import JWT_Icon from '@process-advisor/process-miner/dashboard/JWT.png';
import ScriptRunner_Icon from '@process-advisor/process-miner/dashboard/ScriptRunner.png';
import Jira_Icon from '@process-advisor/process-miner/dashboard/JIRA.png';
import MetricusLogo from '@process-advisor/assets/metricusLogoSmall.png';
import PackDashboard from '@process-advisor/process-miner/consultant/PackDashboard';
import ExportModal from '@process-advisor/process-miner/consultant/ExportModal';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import ConsultantTabHeader, { getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';
import { SaveSnapshotButton, SaveSnapshotModal } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { applySampleItemPrivacy } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';
import AiDataPanel, { AiDataButton } from '@process-advisor/process-miner/consultant/AiDataPanel';
import { buildRovoPackPrompt }  from '@process-advisor/process-miner/consultant/rovoPack-prompt';
import { renderRovoPackHTML, buildRovoPackExportHTML } from '@process-advisor/process-miner/consultant/rovoPack-renderer';
import { REPORT_THEMES, DEFAULT_REPORT_THEME, themeFromPlatform } from '@process-advisor/process-miner/consultant/reportThemes';
import buildAutomationPackPptxDedicated from '@process-advisor/process-miner/consultant/automationPack-pptx';
import Rovo_Icon from '@process-advisor/process-miner/dashboard/Rovo.png';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import useAiReportScopeControls from '@process-advisor/process-miner/consultant/useAiReportScopeControls';
import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
import { outputJSONFormat_Jira } from '@process-advisor/process-miner/consultant/schemas';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};


const outputJSONExample_JWT = {
  pack_id: 'jwt-incident-pack-v1',
  title: 'JWT Automation Pack',
  generated_at: '2026-07-09T00:00:00Z',
  issue_type: 'Incident',
  automation_opportunities: [
    {
      id: 'jwt-rule-001',
      title: 'Auto-close completed incidents after waiting period',
      priority: 'high',
      problem_statement: 'Completed incidents remain open too long, creating administrative delay and distorted cycle-time reporting.',
      goal: 'Automatically close eligible completed incidents after a governed waiting period.',
      applies_to: {
        project_keys: ['PROJECT_KEY'],
        issue_types: ['Incident'],
        workflow_names: ['Incident Workflow']
      },
      related_transitions: ['Completed -> Closed'],
      related_variants: ['Completed -> Closed'],
      evidence_signals: ['Completed -> Closed has high average duration', 'Administrative closure delay is a measurable bottleneck'],
      jwt_implementation: {
        summary: 'Configure JWT controls on the close transition and/or scheduled automation to close stale completed incidents.',
        preconditions: ['JWT for Jira Cloud is installed', 'Closed transition exists', 'Local workflow status names are validated'],
        configuration_steps: [
          'Open the relevant Jira workflow in edit mode.',
          'Select the transition from Completed to Closed.',
          'Add JWT condition/validator/post-function controls using the expressions below.',
          'Publish the workflow and test in a sandbox project before production rollout.'
        ],
        transition_configuration: [
          {
            transition_name: 'Completed -> Closed',
            validators: ['JWT expression validator: issue.status.name == "Completed"'],
            conditions: ['Only allow automatic closure when issue has been in Completed for the configured threshold'],
            post_functions: ['Set resolution and add audit comment before closing']
          }
        ],
        scheduled_rules: [
          {
            name: 'Close stale completed incidents',
            cron_expression: '0 0 9 * * ?',
            schedule_description: 'Run every weekday morning',
            jql_scope: 'issuetype = Incident AND status = Completed AND updated <= -5d',
            actions: ['Transition matching issues to Closed', 'Add audit comment']
          }
        ],
        expression_snippets: [
          {
            purpose: 'Eligibility check',
            expression: 'issue.status.name == "Completed" && issue.updated < now.minusDays(5)'
          }
        ]
      },
      success_criteria: ['Reduced Completed-to-Closed delay', 'Fewer stale completed incidents'],
      expected_impact: {
        impact_summary: 'Reduces administrative ageing and improves cycle-time accuracy.',
        kpis_affected: ['Average duration', 'Admin time', 'Closed backlog ageing'],
        impact_direction: 'positive'
      },
      risk_and_considerations: {
        risk_level: 'medium',
        risks: ['Workflow transition names may differ by project', 'Auto-close policy should be agreed with service owners'],
        rollout_recommendations: ['Pilot in one project', 'Start with comments/audit trail enabled', 'Monitor reopened incidents']
      }
    }
  ]
};

const isJwtSchemaOnlyResponse = (json) => {
  return !!(
    json &&
    typeof json === 'object' &&
    !Array.isArray(json) &&
    (json.properties || json.$schema || json.type === 'object') &&
    !Array.isArray(json.rules) &&
    !Array.isArray(json.automation_opportunities)
  );
};

const toList = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
};

// JWT now uses the same pack shape as ScriptRunner and JMWE: rules[].
// Legacy automation_opportunities responses are normalised once so display/export
// logic can treat JWT like the other workflow automation apps.
const normaliseJwtPackToRules = (json) => {
  if (isJwtSchemaOnlyResponse(json)) {
    throw new Error('JWT generation returned a schema instead of a generated automation pack.');
  }
  if (!json || typeof json !== 'object') {
    throw new Error('JWT generation did not return a JSON object.');
  }

  if (Array.isArray(json.rules)) {
    return { ...json, pack_engine: 'jwt', rules: json.rules };
  }

  if (Array.isArray(json.automation_opportunities)) {
    const rules = json.automation_opportunities.map((op, idx) => {
      const impl = op.jwt_implementation || {};
      const transitions = toList(impl.transition_configuration).map(t => {
        const name = t?.transition_name || '';
        const parts = String(name).split(/\s*(?:->|→)\s*/);
        return { from: parts[0] || '*', to: parts[1] || name || '*' };
      });
      const scheduled = toList(impl.scheduled_rules).map(r => ({
        type: 'scheduled_rule',
        name: r?.name || 'Scheduled rule',
        config: {
          cron_expression: r?.cron_expression || '',
          schedule_description: r?.schedule_description || '',
          jql_scope: r?.jql_scope || '',
          actions: toList(r?.actions),
        },
      }));
      const transitionActions = toList(impl.transition_configuration).flatMap(t => [
        ...toList(t?.validators).map(v => ({ type: 'validator', name: String(v), config: { transition: t?.transition_name || '' } })),
        ...toList(t?.conditions).map(v => ({ type: 'condition', name: String(v), config: { transition: t?.transition_name || '' } })),
        ...toList(t?.post_functions).map(v => ({ type: 'post_function', name: String(v), config: { transition: t?.transition_name || '' } })),
      ]);
      const expressionActions = toList(impl.expression_snippets).map(e => ({
        type: 'expression',
        name: e?.purpose || e?.label || 'JWT expression',
        config: { expression: e?.expression || String(e || '') },
      }));
      return {
        rule_id: op.id || `JWT-${idx + 1}`,
        name: op.title || op.name || 'Unnamed JWT Rule',
        priority: op.priority || op.risk_and_considerations?.risk_level || 'medium',
        applies_to: op.applies_to || {},
        target_transitions: transitions,
        preconditions: { notes: toList(impl.preconditions).join(' · ') },
        jwt_actions: [...transitionActions, ...scheduled, ...expressionActions].filter(Boolean),
        success_criteria: toList(op.success_criteria),
        references: {
          transitions: toList(op.related_transitions),
          variants: toList(op.related_variants),
          evidence: toList(op.evidence_signals),
        },
        rationale: op.problem_statement || op.goal || impl.summary || op.expected_impact?.impact_summary || '',
        risks: [
          ...toList(op.risk_and_considerations?.risks),
          ...toList(op.risk_and_considerations?.rollout_recommendations),
        ],
      };
    });
    const { automation_opportunities, ...rest } = json;
    return { ...rest, pack_engine: 'jwt', rules };
  }

  throw new Error('JWT generation did not return a rules array.');
};

const ensureJwtPackInstance = normaliseJwtPackToRules;


// ── Canonical pack normalisation ────────────────────────────────────────────
// All specialist workflow packs now use the same top-level contract:
// { pack_engine, title, rules: [...] }. The renderer and PPT exporter should
// never have to care whether the pack came from Forge queueing or direct Gemini.
const isSchemaOnlyAutomationResponse = (json) => !!(
  json && typeof json === 'object' && !Array.isArray(json) &&
  (json.$schema || json.properties || json.type === 'object') &&
  !Array.isArray(json.rules) && !Array.isArray(json.automation_opportunities)
);

const normaliseRuleArray = (rules, engine) => toList(rules).map((rule, idx) => {
  const r = rule && typeof rule === 'object' ? { ...rule } : { name: String(rule || `Rule ${idx + 1}`) };
  const prefix = engine === 'scriptrunner' ? 'SR' : engine === 'jmwe' ? 'JMWE' : engine === 'jwt' ? 'JWT' : 'RULE';
  r.rule_id = r.rule_id || r.id || `${prefix}-RULE-${String(idx + 1).padStart(3, '0')}`;
  r.name = r.name || r.title || `${prefix} Automation ${idx + 1}`;
  r.priority = r.priority || r.risk_level || 'medium';
  r.rationale = r.rationale || r.intent || r.description || r.problem_statement || r.goal || '';
  r.success_criteria = toList(r.success_criteria);
  r.risks = toList(r.risks || r.risk_notes || r.rollout_risks);
  r.applies_to = r.applies_to || { issue_types: [] };
  r.target_transitions = toList(r.target_transitions || r.related_transitions).map((t) => {
    if (t && typeof t === 'object') return t;
    const txt = String(t || '');
    const parts = txt.split(/\s*(?:->|→|- )\s*/).filter(Boolean);
    return parts.length >= 2 ? { from: parts[0], to: parts.slice(1).join(' → ') } : { from: txt || '*', to: '*' };
  });
  r.preconditions = r.preconditions || (r.jql_scope ? { jql: r.jql_scope } : {});

  // Keep each app's app-specific implementation key, but tolerate the model
  // returning a generic actions array.
  if (engine === 'jmwe' && !Array.isArray(r.jmwe_actions)) {
    r.jmwe_actions = toList(r.actions).map((a) => typeof a === 'object' ? a : ({ type: 'post_function', name: String(a || 'JMWE action'), config: {} }));
  }
  if (engine === 'jwt' && !Array.isArray(r.jwt_actions)) {
    r.jwt_actions = toList(r.actions).map((a) => typeof a === 'object' ? a : ({ type: 'post_function', name: String(a || 'JWT action'), config: {} }));
  }
  if (engine === 'scriptrunner') {
    r.actions = toList(r.actions).map((a) => typeof a === 'string' ? a : (a?.name || a?.type || JSON.stringify(a)));
    r.when = toList(r.when || r.conditions);
    r.script = r.script || { language: 'groovy', pseudo: r.pseudo || r.rationale || '', code: r.code || '' };
  }
  return r;
});

const normaliseAutomationPackForEngine = (json, engine) => {
  if (engine === 'jwt') return ensureJwtPackInstance(json);
  if (isSchemaOnlyAutomationResponse(json)) {
    throw new Error(`${String(engine || 'Automation pack').toUpperCase()} generation returned a schema instead of generated rules.`);
  }
  if (!json || typeof json !== 'object') {
    throw new Error(`${String(engine || 'Automation pack').toUpperCase()} generation did not return a JSON object.`);
  }
  if (!Array.isArray(json.rules)) {
    throw new Error(`${String(engine || 'Automation pack').toUpperCase()} generation did not return a rules array.`);
  }
  return {
    ...json,
    pack_engine: json.pack_engine || engine,
    title: json.title || `${engine} Automation Pack`,
    rules: normaliseRuleArray(json.rules, engine),
  };
};

const createEmptyWorkflowPack = (engine, label) => ({
  pack_id: `${engine}-pack-empty`,
  pack_engine: engine,
  title: `${label} Automation Pack`,
  generated_at: '',
  jira_deployment: 'cloud',
  project_keys: [],
  rules: [],
});

// ── Cross-pack fix themes ────────────────────────────────────────────────────
// The Jira workflow packs (A4J, ScriptRunner, JMWE, JWT) are alternative
// implementation surfaces for the same underlying process fixes. Every rule
// carries a fix_theme so the UI can flag when the same fix exists in more
// than one pack, and so thresholds stay consistent across packs.
const SHARED_FIX_THEME_RULES = `CROSS-PACK FIX THEME RULES
- Each rule must include a "fix_theme" field: a short kebab-case identifier of the underlying process fix, independent of the app implementing it.
- Use these canonical themes when they match the evidence, and invent a new kebab-case theme only when none fits: auto-close-stale-completed | escalate-stale-pending | require-pause-reason | prevent-reopen-regression | auto-assign-by-classification | enforce-closure-data | flag-rework-loop | notify-aging-items | require-triage-fields.
- Time thresholds must be derived from the evidence and expressed in whole days. For stale-item rules, use the observed average delay for that transition rounded to the nearest whole day.
- These packs are alternative implementation surfaces for the same process fixes. A rule sharing a fix_theme with a rule in another app's pack must use the same threshold and target the same transition, because both are derived from the same evidence. Do not tailor thresholds per app.`;

const buildWorkflowPackOutputExample = (engine, label, issueTypeStr) => {
  const appActionKey = engine === 'jmwe' ? 'jmwe_actions' : engine === 'jwt' ? 'jwt_actions' : null;
  const baseRule = {
    rule_id: `${label.toUpperCase().replace(/[^A-Z0-9]+/g, '-')}-RULE-001`,
    name: `Client-ready ${label} automation name`,
    priority: 'high',
    applies_to: { project_keys: ['PROJECT_KEY'], issue_types: [issueTypeStr], workflow_names: ['Workflow name if known'] },
    target_transitions: [{ from: 'Status A', to: 'Status B' }],
    preconditions: { jql: `issuetype = '${issueTypeStr}'`, notes: 'Local field/status names must be validated before rollout.' },
    success_criteria: ['Measurable process outcome tied to the evidence'],
    references: { transitions: ['Evidence transition'], variants: ['Evidence variant'] },
    rationale: `Evidence-backed reason this ${label} rule is needed.`,
    fix_theme: 'auto-close-stale-completed',
    risks: ['Rollout or workflow risk'],
  };
  if (engine === 'scriptrunner') {
    baseRule.intent = `What the ${label} rule does and why.`;
    baseRule.trigger = { type: 'listener', events: ['jira:issue_created'] };
    baseRule.jql_scope = `issuetype = '${issueTypeStr}'`;
    baseRule.when = ['Condition that must be true'];
    baseRule.actions = ['Concrete action taken by the script'];
    baseRule.script = { language: 'groovy', pseudo: 'Plain English logic summary.', code: '// ScriptRunner for Jira Cloud Groovy goes here' };
  } else {
    baseRule[appActionKey] = [{ type: 'validator', name: `${label} field required validator`, config: { field: 'Field name', errorMessage: 'User-facing validation message' } }];
  }
  return {
    pack_id: `${engine}-pack-id`,
    pack_engine: engine,
    title: `${label} Automation Pack`,
    generated_at: '2026-07-09T00:00:00Z',
    jira_deployment: 'cloud',
    project_keys: ['PROJECT_KEY'],
    rules: [baseRule],
  };
};

const SLEEP_DELAY_MS = 50;
const sleep = ms => new Promise(r => setTimeout(r, ms));

// ── Normalise provider string for invoke ─────────────────────────────────────
const toInvokeProvider = (provider) =>
  provider === 'gemini' ? 'Gemini'
  : provider === 'claude' ? 'Claude'
  : 'ChatGPT';

const replaceQuotes = (obj) => {
  for (let key in obj) {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].replace(/\"([^\"]+)\"/g, "'$1'");
    } else if (typeof obj[key] === 'object') {
      replaceQuotes(obj[key]);
    }
  }
};

const stripMarkdownFences = (raw) => {
  if (typeof raw !== 'string') return raw;
  return raw
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/i, '')
    .trim();
};

const extractFirstJsonBlock = (raw) => {
  if (typeof raw !== 'string') return raw;
  const text = stripMarkdownFences(raw);
  const start = text.search(/[\[{]/);
  if (start === -1) return text;

  const opener = text[start];
  const closer = opener === '{' ? '}' : ']';
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = start; i < text.length; i++) {
    const ch = text[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === opener) depth++;
    if (ch === closer) {
      depth--;
      if (depth === 0) return text.slice(start, i + 1);
    }
  }

  return text.slice(start);
};

const repairLooseJsonText = (text) => {
  let out = String(text || '')
    .replace(/^\uFEFF/, '')
    .replace(/,\s*([}\]])/g, '$1')
    .trim();

  // Some models still return a short lead-in or a trailing explanation. Keep the
  // largest likely JSON object before attempting repair.
  out = extractFirstJsonBlock(out);

  // JSON.parse fails when the model emits literal line breaks/tabs inside a
  // quoted value, or when it uses unescaped double quotes inside prose such as
  // `open the "Validators" tab`. Repair those cases while preserving valid JSON
  // key/value delimiters.
  let repaired = '';
  let inString = false;
  let escaped = false;

  const nextNonSpace = (idx) => {
    for (let j = idx + 1; j < out.length; j++) {
      const c = out[j];
      if (!/\s/.test(c)) return c;
    }
    return '';
  };

  for (let i = 0; i < out.length; i++) {
    const ch = out[i];

    if (escaped) {
      repaired += ch;
      escaped = false;
      continue;
    }

    if (ch === '\\') {
      repaired += ch;
      escaped = true;
      continue;
    }

    if (ch === '"') {
      if (!inString) {
        inString = true;
        repaired += ch;
        continue;
      }

      const next = nextNonSpace(i);
      const isJsonBoundaryQuote = next === ':' || next === ',' || next === '}' || next === ']' || next === '';
      if (isJsonBoundaryQuote) {
        inString = false;
        repaired += ch;
      } else {
        repaired += '\\"';
      }
      continue;
    }

    if (inString && ch === '\n') { repaired += '\\n'; continue; }
    if (inString && ch === '\r') { repaired += '\\r'; continue; }
    if (inString && ch === '\t') { repaired += '\\t'; continue; }
    repaired += ch;
  }

  return repaired.replace(/,\s*([}\]])/g, '$1');
};

const parseAIJsonResponse = (raw) => {
  if (raw == null) throw new Error('Empty AI response');
  if (typeof raw !== 'string') return raw;

  const cleaned = stripMarkdownFences(raw);
  const attempts = [cleaned, extractFirstJsonBlock(cleaned), repairLooseJsonText(cleaned)];
  let lastError = null;

  for (const candidate of attempts) {
    if (!candidate || !String(candidate).trim()) continue;
    try {
      return JSON.parse(candidate);
    } catch (err) {
      lastError = err;
    }
  }

  console.error('Raw AI JSON parse failure:', raw);
  throw lastError || new Error('Unable to parse AI JSON response');
};

let processingCtr = 0;

const AutomationPacks = ({ aiModelConfig }) => {
  const modelId = getModelID();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [activeView, setActiveView] = useState('grid');
  const [JMWE, setJMWE] = useState(() => createEmptyWorkflowPack('jmwe', 'JMWE'));
  const [JMWEGenerated, setJMWEGenerated] = useState(false);
  const [JWT, setJWT] = useState(() => createEmptyWorkflowPack('jwt', 'JWT'));
  const [JWTGenerated, setJWTGenerated] = useState(false);
  const [modelTitle, setModelTitle] = useState('');
  const [activities, setActivities] = useState();
  const [exportType, setExportType] = useState(null);
  const [scriptRunnerData, setScriptRunnerData] = useState(() => createEmptyWorkflowPack('scriptrunner', 'ScriptRunner'));
  const [scriptRunnerGenerated, setScriptRunnerGenerated] = useState(false);
  const [jiraData, setJiraData] = useState(outputJSONFormat_Jira);
  const [jiraGenerated, setJiraGenerated] = useState(false);
  const [rovoData, setRovoData] = useState(null);
  const [rovoGenerated, setRovoGenerated] = useState(false);
  const [serviceNowPackData, setServiceNowPackData] = useState({});
  const [serviceNowGenerated, setServiceNowGenerated] = useState({});
  const [transitionsJSON, setTransitionsJSON] = useState([]);
  const [variantsJSON, setVariantsJSON] = useState([]);

  const aiScope = useAiReportScopeControls({
    modelId,
    reportKey: 'automationPacks',
    reportLabel: 'AI & Automation Packs',
    getTaskRows: taskJSON,
    getVariants: () => apiProvider.variantsTable() || [],
    getProcessSteps: () => apiProvider.processStepsTable() || [],
    getPrivacyRules: getAttributePrivacyRules,
  });

  // ── Resolve AI config (always Gemini 3.1 Pro Preview by default) ─────────────────
  const resolvedAiModelConfig = getAIModelConfig(aiModelConfig);
  const aiProvider    = resolvedAiModelConfig.provider;
  const selectedModel = resolvedAiModelConfig.model;
  const temperature   = resolvedAiModelConfig.temperature;

  const [requestType, setRequestType] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const aiProgressTimerRef = useRef(null);
  let _ctr = 0;

  useEffect(() => {
    setModelTitle(getProcessModelTitle());
    setActivities(apiProvider.processActivitiesTable() || []);

    const transitions = apiProvider.processStepsTable() || [];
    const tempTransitions = transitions.slice(0, 40).map(t => ({
      'Transition':    t.step,
      'From Activity': t.from_activity,
      'To Activity':   t.to_activity,
      'Work items':    t.tasks,
      'Avg deviation (hrs)': t.avg_duration_hrs?.toFixed(2) ?? 'N/A',
      'Std deviation (hrs)': t.std_deviation_hrs?.toFixed(2) ?? 'N/A',
      [`% total ${apiProvider.getEntityUpper().toLowerCase()}s`]:
        t.perc_total_tasks != null ? (t.perc_total_tasks * 100).toFixed(2) : 'N/A',
      'Is rework?': t.loop,
    }));
    setTransitionsJSON(tempTransitions);

    const variantsSrc = apiProvider.variantsTable() || [];
    const tempVariants = variantsSrc.slice(0, 30).map(v => ({
      Variant: v.variant,
      [apiProvider.getEntityUpper() + 's']: v.tasks,
      ['% Total ' + apiProvider.getEntityUpper() + 's']:
        v.perc_total_tasks != null ? (Number(v.perc_total_tasks) * 100).toFixed(2) : 'N/A',
      'Is Rework': v.loop,
      Loops: v.loops,
      'Std Deviation (hrs)': v.std_deviation_hrs != null ? Number(v.std_deviation_hrs).toFixed(2) : 'N/A',
      'Avg Duration (hrs)':  v.avg_duration_hrs  != null ? Number(v.avg_duration_hrs).toFixed(2)  : 'N/A',
    }));
    setVariantsJSON(tempVariants);
  }, []);

  // ── Shared data helpers (same as other consultant tabs) ────────────────────
  const getSharedData = () => {
    const issueTypes   = getIssueTypes();
    const issueTypeStr = issueTypes.length === 1
      ? `Work Type = ${issueTypes[0]}`
      : `Work Types = ${issueTypes.join(' or ')}`;

    const metricsHistory = getModelMetrics().filter(m => m.work_items > 0).map(m => ({
      [apiProvider.getEntityUpper() + 's']: m.work_items,
      Variants:          m.variants,
      Activities:        m.activities,
      Steps:             m.steps,
      'Avg Transitions': m.avg_transitions.toFixed(1),
      'Avg Duration':    (m.cycle_time    / 3600).toFixed(2),
      'Std Deviation':   (m.std_deviation / 3600).toFixed(2),
      '% Rework':        (m.rework_rate   * 100 ).toFixed(2),
    }));

    return {
      issueTypeStr,
      metricsHistory,
      metricsTotal: typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null,
      sampleItems:        applySampleItemPrivacy(aiScope.getScopedTaskRows(), getAttributePrivacyRules()).rows,
      transitionsJSON:    (aiScope.getScopedProcessSteps() || []).slice(0, 40).map(t => ({
        'Transition':    t.step,
        'From Activity': t.from_activity,
        'To Activity':   t.to_activity,
        'Work items':    t.tasks,
        'Avg deviation (hrs)': t.avg_duration_hrs?.toFixed(2) ?? 'N/A',
        'Std deviation (hrs)': t.std_deviation_hrs?.toFixed(2) ?? 'N/A',
        [`% total ${apiProvider.getEntityUpper().toLowerCase()}s`]:
          t.perc_total_tasks != null ? (t.perc_total_tasks * 100).toFixed(2) : 'N/A',
        'Is rework?': t.loop,
      })),
      variantsJSON:      (aiScope.getScopedVariants() || []).slice(0, 30).map(v => ({
        Variant: v.variant,
        [apiProvider.getEntityUpper() + 's']: v.tasks,
        ['% Total ' + apiProvider.getEntityUpper() + 's']:
          v.perc_total_tasks != null ? (Number(v.perc_total_tasks) * 100).toFixed(2) : 'N/A',
        'Is Rework': v.loop,
        Loops: v.loops,
        'Std Deviation (hrs)': v.std_deviation_hrs != null ? Number(v.std_deviation_hrs).toFixed(2) : 'N/A',
        'Avg Duration (hrs)':  v.avg_duration_hrs  != null ? Number(v.avg_duration_hrs).toFixed(2)  : 'N/A',
      })),
      reportScopeSummary: aiScope.getReportScopeSummary(),
      reportScopeText:    buildReportScopePromptSection(aiScope.getReportScopeSummary()),
      fieldUsage:         getFieldUsage(),
      statusTypeSummary:  getStatusTypeSummary  ? getStatusTypeSummary()  : null,
      statusTypeMappings: getStatusTypeMappings ? getStatusTypeMappings() : [],
      activityType: typeof getActivityType === 'function' ? getActivityType() : null,
      activityContext: typeof getActivityContext === 'function' ? getActivityContext() : null,
    };
  };

  useEffect(() => {
    if (requestId && (status?.status === 'pending' || status?.status === 'processing')) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const { invoke } = await loadForgeModule();
          const statusData = await invoke('checkStatus', { requestId });
          setStatus(statusData);

          if (requestType === 'jmwe')        updateProgressModal({ status: 'JMWE Automation - processing '        + processingCtr, ai: true });
          if (requestType === 'rovo')        updateProgressModal({ status: 'Rovo Agent Pack - processing '        + processingCtr, ai: true });
          if (requestType === 'jwt')         updateProgressModal({ status: 'JWT Automation - processing '         + processingCtr, ai: true });
          if (requestType === 'scriptrunner')updateProgressModal({ status: 'ScriptRunner Automation - processing ' + processingCtr, ai: true });
          if (requestType === 'jira')        updateProgressModal({ status: 'Jira Automation (A4J) - processing '  + processingCtr, ai: true });

          processingCtr++;
          if (statusData.status === 'completed') {
            const insightsJSON = parseAIJsonResponse(statusData.result);
            replaceQuotes(insightsJSON);

            if (requestType === 'jmwe')        { setJMWE(normaliseAutomationPackForEngine(insightsJSON, 'jmwe')); setJMWEGenerated(true); setActiveView('jmwe'); }
            if (requestType === 'jwt')         { const jwtPack = ensureJwtPackInstance(insightsJSON); setJWT(jwtPack); setJWTGenerated(true); setActiveView('jwt'); }
            if (requestType === 'scriptrunner'){ setScriptRunnerData(normaliseAutomationPackForEngine(insightsJSON, 'scriptrunner')); setScriptRunnerGenerated(true); setActiveView('scriptrunner'); }
            if (requestType === 'rovo')        { setRovoData(insightsJSON);         setRovoGenerated(true);          setActiveView('rovo'); }
            if (requestType === 'jira')        { setJiraData(insightsJSON);        setJiraGenerated(true);          setActiveView('jira'); }

            closeProgressModal();
            clearInterval(pollIntervalRef.current);
          } else if (statusData.status === 'failed') {
            setError(statusData.error || 'Request failed');
            clearInterval(pollIntervalRef.current);
          }
        } catch (err) {
          console.error('Error checking status:', err);
          setError('Failed to check status');
          closeProgressModal();
          clearInterval(pollIntervalRef.current);
        }
      }, 1000);

      return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
    }
  }, [requestId, status?.status]);

  // ── Shared Forge invoke helper ────────────────────────────────────────────
  const invokeForge = async (type, prompt) => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    processingCtr = 1;
    setError(null);
    setStatus(null);
    setRequestType(type);
    try {
      const { invoke } = await loadForgeModule();
      const response = await invokeProcessOpenAIWithBilling({
        invoke,
        reportLabel: type || 'Automation Pack',
        payload: {
          prompt,
          aiProvider: toInvokeProvider(aiProvider),
          model: selectedModel,
          temperature,
        },
      });
      setRequestId(response.requestId);
      setStatus({ status: 'pending' });
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request');
    }
  };

  // ── Shared direct fetch helper (Gemini only) ──────────────────────────────────
  const _geminiKey = (typeof API_KEY !== 'undefined' && API_KEY) ? API_KEY : undefined;
  const fetchDirect = async (prompt) => {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${selectedModel}:generateContent?key=${_geminiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt + '\n\nYou must output valid JSON. All JSON string values must use double quotes on the outside, and if the string contains quotes inside, replace them with single quotes instead of escaped double quotes.' }] }],
          generationConfig: { response_mime_type: 'application/json' },
        }),
      }
    );
    const data = await response.json();
    const json = parseAIJsonResponse(data?.candidates?.[0]?.content?.parts?.[0]?.text);
    replaceQuotes(json);
    return json;
  };

  // ── Generate functions ────────────────────────────────────────────────────

  const generateJMWE = async () => {
    await openProgressModal({ status: getAppVersion() === 'FORGE' ? 'JMWE Automation - queueing request' : 'Generating JMWE Automation', ai: true });
    await sleep(SLEEP_DELAY_MS);
    const { issueTypeStr, metricsHistory, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, statusTypeMappings, activityType, reportScopeText } = getSharedData();
    const prompt = `You are an expert Jira Misc Workflow Extensions (JMWE) consultant. Based on process mining evidence below, generate an implementation-ready JMWE automation pack for Jira Cloud.

JMWE IMPLEMENTATION RULES
- Do not produce discovery_rules_summary or any process analysis — the user has seen that in Process Health. Produce rules only.
- Each rule must include actual JMWE post-function/validator/condition configuration — not descriptions.
- For post-functions: specify the exact JMWE function name (e.g. "Set Field Value", "Transition Issue", "Send Email") and the Nunjucks template expressions where applicable.
- For Nunjucks expressions use JMWE syntax: {{issue.summary}}, {{issue.status.name}}, {{#jql}}...{{/jql}}, {{now | date: 'yyyy-MM-dd'}}.
- jql_scope must be a valid JQL string based on the observed issue type: ${issueTypeStr}.
- Use field_usage to reference only fields that are actually populated in this project.
- Use metrics_history rework rate and cycle time values to size the impact of each rule.
- Use activity context/type mappings to identify active, waiting, queue, routing, or ownership signals. Do not assume activities are statuses unless the activity field is status/state.
- Provide at least 6 rules grounded in the evidence signals below.

${SHARED_FIX_THEME_RULES}

OUTPUT: Return ONLY a strict JSON object. No markdown fences. No commentary. No trailing text.
Use this exact top-level shape and field names. Generate a populated JSON instance, not a schema.
${JSON.stringify(buildWorkflowPackOutputExample('jmwe', 'JMWE', issueTypeStr), null, 2)}

${reportScopeText}
PROCESS EVIDENCE
PROCESS EVIDENCE
Issue type: ${issueTypeStr}
Metrics history: ${JSON.stringify(metricsHistory)}
Transitions (${transitionsJSON.length}): ${JSON.stringify(transitionsJSON)}
Variants (${variantsJSON.length}): ${JSON.stringify(variantsJSON)}
Sample items: ${JSON.stringify(sampleItems)}
Field usage: ${JSON.stringify(fieldUsage)}
Activity context: ${JSON.stringify({ activity_field: activityType, activity_label: activityType })}
Activity type summary: ${JSON.stringify(statusTypeSummary)}`;
    if (getAppVersion() === 'FORGE') {
      await invokeForge('jmwe', prompt);
    }
    if (getAppVersion() === 'SNOW') {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      aiProgressTimerRef.current = await startAiProgressTimer('Generating JMWE Automation');
      try {
        const json = await fetchDirect(prompt);
        setJMWE(normaliseAutomationPackForEngine(json, 'jmwe')); setJMWEGenerated(true); setActiveView('jmwe');
      } catch (error) {
        console.error('Error generating insights:', error);
        alert('Failed to generate insights. Please try again.');
      } finally { stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null; clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; closeProgressModal(); }
    }
  };

  const generateJWT = async () => {
    await openProgressModal({ status: getAppVersion() === 'FORGE' ? 'JWT Automation - queueing request' : 'Generating JWT Automation' });
    await sleep(SLEEP_DELAY_MS);
    const { issueTypeStr, metricsHistory, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, statusTypeMappings, activityType, reportScopeText } = getSharedData();
    const prompt = `You are an expert Jira Workflow Toolbox (JWT) consultant. Based on process mining evidence below, generate an implementation-ready JWT automation pack for Jira Cloud.

JWT IMPLEMENTATION RULES
- Generate JWT exactly like the JMWE and ScriptRunner packs: one top-level pack with a rules array.
- Do not produce automation_opportunities, global_insights, context_summary, or schema metadata.
- Each rule must be implementation-ready and include rationale, target transitions, concrete JWT actions, success criteria, and risks.
- Use the term JWT / Jira Workflow Toolbox instead of JMWE or ScriptRunner.
- jwt_actions must contain concrete JWT configuration items. Valid action types are: validator, condition, post_function, scheduled_rule, expression.
- For validators/conditions/post-functions, specify the JWT function name and config values.
- For scheduled rules, include cron_expression, jql_scope, and actions in config.
- For expressions, include a copy-ready expression in config.expression.
- jql_scope values must be valid JQL for the observed issue type: ${issueTypeStr}.
- Use field_usage to reference only fields that exist in this project.
- Use metrics_history, transitions, variants, and activity context to justify each rule.
- Provide at least 6 rules grounded in the evidence signals below.

${SHARED_FIX_THEME_RULES}

OUTPUT: Return ONLY a strict JSON object. No markdown fences. No commentary. No trailing text.
Use this exact top-level shape and field names. Generate a populated JSON instance, not a schema.
${JSON.stringify(buildWorkflowPackOutputExample('jwt', 'JWT', issueTypeStr), null, 2)}

All strings must be valid JSON strings. Use single quotes inside prose instead of unescaped double quotes.

${reportScopeText}
PROCESS EVIDENCE
Issue type: ${issueTypeStr}
Metrics history: ${JSON.stringify(metricsHistory)}
Transitions (${transitionsJSON.length}): ${JSON.stringify(transitionsJSON)}
Variants (${variantsJSON.length}): ${JSON.stringify(variantsJSON)}
Sample items: ${JSON.stringify(sampleItems)}
Field usage: ${JSON.stringify(fieldUsage)}
Activity context: ${JSON.stringify({ activity_field: activityType, activity_label: activityType })}
Activity type summary: ${JSON.stringify(statusTypeSummary)}`;
    if (getAppVersion() === 'FORGE') {
      await invokeForge('jwt', prompt);
    }
    if (getAppVersion() === 'SNOW') {
      try {
        const json = ensureJwtPackInstance(await fetchDirect(prompt));
        setJWT(json); setJWTGenerated(true); setActiveView('jwt');
      } catch (error) {
        console.error('Error generating insights:', error);
        alert('Failed to generate insights. Please try again.');
      } finally { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; closeProgressModal(); }
    }
  };

  const generateScriptRunner = async () => {
    await openProgressModal({ status: getAppVersion() === 'FORGE' ? 'ScriptRunner Automation - queueing request' : 'Generating ScriptRunner Automation' });
    await sleep(SLEEP_DELAY_MS);
    const { issueTypeStr, metricsHistory, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, statusTypeMappings, activityType, reportScopeText } = getSharedData();
    const prompt = `You are an expert ScriptRunner for Jira (Cloud) consultant. Based on process mining evidence below, generate an implementation-ready ScriptRunner automation pack.

SCRIPTRUNNER CLOUD IMPLEMENTATION RULES
- Do not produce discovery_rules_summary or any process analysis — go straight to rules with real executable code.
- ScriptRunner for Jira Cloud scripts (Script Console, Script Listeners, Scheduled Jobs) are written in Groovy. JavaScript is only used for the Behaviours feature, which is out of scope for this pack. The language field must be "groovy".
- The script.code field must contain actual runnable Groovy using the auto-imported Unirest-style REST helpers — get(...), post(...), put(...), delete(...) against the Jira Cloud REST API. Do NOT use Forge modules (anything like require('@forge/...')), the Jira Server/DC Java API (com.atlassian.jira.*), or ScriptRunner DC classes — none of these exist in ScriptRunner for Jira Cloud.
- REST call style (follow exactly): def result = post("/rest/api/3/issue/\${issueKey}/transitions").header('Content-Type', 'application/json').body([transition: [id: '31']]).asObject(Map); then check result.status (204/200/201) before continuing.
- To find issues by JQL: def search = post('/rest/api/3/search/jql').header('Content-Type', 'application/json').body([jql: jql, fields: ['status','assignee']]).asObject(Map); iterate search.body.issues and paginate with nextPageToken when present.
- Resolve transition ids at runtime via get("/rest/api/3/issue/\${issueKey}/transitions") rather than hard-coding ids, unless the workflow is known.
- For scheduled rules: trigger.type "scheduled" with a valid cron expression in trigger.schedule; implement as a ScriptRunner Scheduled Job.
- For listeners: trigger.type "listener" and trigger.events must list valid Jira webhook event names (e.g. "jira:issue_created", "jira:issue_updated").
- jql_scope must be valid JQL for: ${issueTypeStr}.
- Use field_usage to identify which fields are available to query or set in scripts.
- Use metrics_history to size the problem each script addresses.
- Use activity context/type mappings to reference workflow states only when the activity field is status/state; otherwise treat activity values as the selected field values.
- The pseudo field should be a plain English summary; the code field is the real executable script.
- Provide at least 6 rules grounded in the evidence signals below.

${SHARED_FIX_THEME_RULES}

OUTPUT: Return ONLY a strict JSON object. No markdown fences. No commentary. No trailing text.
Use this exact top-level shape and field names. Generate a populated JSON instance, not a schema.
${JSON.stringify(buildWorkflowPackOutputExample('scriptrunner', 'ScriptRunner', issueTypeStr), null, 2)}

${reportScopeText}
PROCESS EVIDENCE
PROCESS EVIDENCE
Issue type: ${issueTypeStr}
Metrics history: ${JSON.stringify(metricsHistory)}
Transitions (${transitionsJSON.length}): ${JSON.stringify(transitionsJSON)}
Variants (${variantsJSON.length}): ${JSON.stringify(variantsJSON)}
Sample items: ${JSON.stringify(sampleItems)}
Field usage: ${JSON.stringify(fieldUsage)}
Activity context: ${JSON.stringify({ activity_field: activityType, activity_label: activityType })}
Activity type summary: ${JSON.stringify(statusTypeSummary)}`;
    if (getAppVersion() === 'FORGE') {
      await invokeForge('scriptrunner', prompt);
    }
    if (getAppVersion() === 'SNOW') {
      try {
        const json = await fetchDirect(prompt);
        setScriptRunnerData(normaliseAutomationPackForEngine(json, 'scriptrunner')); setScriptRunnerGenerated(true); setActiveView('scriptrunner');
      } catch (error) {
        console.error('Error generating ScriptRunner analysis:', error);
        alert('Failed to generate ScriptRunner analysis. Please try again.');
      } finally { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; closeProgressModal(); }
    }
  };

  const generateJira = async () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    processingCtr = 0;
    await openProgressModal({ status: getAppVersion() === 'FORGE' ? 'Jira Automation (A4J) - queueing request' : 'Generating Jira Automation (A4J)' });
    await sleep(SLEEP_DELAY_MS);
    const { issueTypeStr, metricsHistory, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, statusTypeMappings, activityType, reportScopeText } = getSharedData();
    const prompt = `You are an expert Jira Automation (A4J) consultant. Based on process mining evidence below, generate an implementation-ready Jira Automation pack for Jira Cloud that can be imported directly.

JIRA AUTOMATION IMPLEMENTATION RULES
- Do not produce insights, key_discovery_rules, or hotspots — go straight to rules with real importable JSON.
- The export.a4j_cloud field for each rule must contain a valid Jira Automation Cloud export object that can be imported via Project Settings → Automation → Import.
- Use this exact Jira Automation Cloud export structure for a4j_cloud:
  { "name": "...", "state": "ENABLED", "canOtherRuleTriggerThis": false, "shareTeam": false, "tags": [], "ruleScope": { "resources": ["ari:cloud:jira::site/SITE-ID"] }, "trigger": { "component": "TRIGGER", "type": "jira.issue.transitioned | jira.issue.created | jira.jql.scheduled", "value": {} }, "components": [ { "component": "CONDITION", "type": "jira.issue.condition.jql", "value": { "queryString": "..." } }, { "component": "ACTION", "type": "jira.issue.field.edit | jira.issue.transition | jira.comment.add", "value": { ... } } ] }
- triggers.config for scheduled rules must include: { "cron": "0 0 9 ? * MON-FRI *" } style cron.
- conditions.config for JQL conditions: { "queryString": "status = 'In Progress' AND updated <= -2d AND issuetype = '${issueTypeStr}'" }.
- actions.config for field edits: { "fields": { "fieldId": "assignee", "value": { "type": "AUTO" } } }.
- actions.config for transitions: { "transition": { "name": "Close Issue" } }.
- Provide at least 6 rules grounded in the evidence signals below.
- Use field_usage to write accurate JQL conditions referencing only fields that exist.
- Use metrics_history rework rates and cycle times to justify trigger thresholds.
- Use activity context/type mappings to identify the correct activity values. Only use status JQL when the activity field is status/state.
- Use sample_items to understand actual field values when writing conditions.

${SHARED_FIX_THEME_RULES}

OUTPUT FORMAT: Return ONLY a JSON object with actual automation rule data. Do NOT return the schema definition itself. Your response must contain real rules in the "rules" array with actual trigger/action configurations based on the evidence below. The rules array must not be empty.

REQUIRED STRUCTURE (fill with real data):
{
  "version": "1.0.0",
  "generated_at": "2024-01-15T10:00:00Z",
  "source_context": {
    "issue_type": "${issueTypeStr}",
    "analysis_summary": "Brief summary of the workflow issues"
  },
  "rules": [
    {
      "id": "a4j-rule-001",
      "name": "Real rule name based on evidence",
      "description": "What this rule does",
      "risk_level": "low|medium|high",
      "fix_theme": "auto-close-stale-completed",
      "triggers": [{ "type": "issue_transitioned", "config": {...} }],
      "actions": [{ "type": "add_comment", "config": {...} }],
      "export": { "a4j_cloud": { actual A4J export JSON } }
    }
  ]
}

${reportScopeText}
PROCESS EVIDENCE
PROCESS EVIDENCE
Issue type: ${issueTypeStr}
Metrics history: ${JSON.stringify(metricsHistory)}
Transitions (${transitionsJSON.length}): ${JSON.stringify(transitionsJSON)}
Variants (${variantsJSON.length}): ${JSON.stringify(variantsJSON)}
Sample items: ${JSON.stringify(sampleItems)}
Field usage: ${JSON.stringify(fieldUsage)}
Activity context: ${JSON.stringify({ activity_field: activityType, activity_label: activityType })}
Activity type summary: ${JSON.stringify(statusTypeSummary)}`;
    if (getAppVersion() === 'FORGE') {
      await invokeForge('jira', prompt);
    }
    if (getAppVersion() === 'SNOW') {
      try {
        const json = await fetchDirect(prompt);
        setJiraData(json); setJiraGenerated(true); setActiveView('jira');
      } catch (error) {
        console.error('Error generating Jira analysis:', error);
        alert('Failed to generate Jira analysis. Please try again.');
      } finally { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; closeProgressModal(); }
    }
  };

  const generateRovo = async () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    processingCtr = 0;
    await openProgressModal({ status: getAppVersion() === 'FORGE' ? 'Rovo Agent Pack - queueing request' : 'Generating Rovo Agent Pack', ai: true });
    await sleep(SLEEP_DELAY_MS);
    const { issueTypeStr, metricsHistory, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, activityType, reportScopeSummary } = getSharedData();
    const prompt = await injectOrganizationContext(buildRovoPackPrompt({ transitionsJSON, variantsJSON, issueTypeStr, metricsHistory, sampleItems, fieldUsage, statusTypeSummary, activityType, reportScopeSummary }));
    if (getAppVersion() === 'FORGE') {
      await invokeForge('rovo', prompt);
    }
    if (getAppVersion() === 'SNOW') {
      try {
        const json = await fetchDirect(prompt);
        setRovoData(json); setRovoGenerated(true); setActiveView('rovo');
      } catch (error) {
        console.error('Error generating Rovo pack:', error);
        alert('Failed to generate Rovo Agent Pack. Please try again.');
      } finally { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; closeProgressModal(); }
    }
  };


  const serviceNowPackDefinitions = [
    {
      key: 'sn_flow_designer',
      label: 'Flow Designer Pack',
      packTitle: 'ServiceNow Flow Designer Pack',
      progressTitle: 'Generating Flow Designer Pack',
      sublabel: 'Event-driven and scheduled flows for delays, handoffs, reminders and repetitive work',
      accent: '#0A4A57',
      capabilityFocus: 'Flow Designer, Workflow Studio, IntegrationHub actions, notifications, scheduled triggers, timers, approvals, and reusable subflows',
      rulePrefix: 'sn-flow',
      technicalRequirements: [
        { requirement: 'Flow Designer is core Now Platform — standard flows need no additional licensing', type: 'Licensing' },
        { requirement: 'IntegrationHub spokes/actions beyond the bundled starter pack require an IntegrationHub subscription — verify entitlement before recommending spoke actions', type: 'Licensing' },
        { requirement: 'flow_designer or admin role to build and activate flows', type: 'Permission' },
        { requirement: 'Build in scope where possible; validate in sub-production before promoting via update sets or application repository', type: 'Deployment' },
      ],
      guidance: 'Generate only Flow Designer-style automations: triggers, branches, waits, notifications, approvals, IntegrationHub actions, and reusable subflows. Do not recommend assignment rules, SLA definition changes, Virtual Agent topics, or Predictive Intelligence models in this pack unless they are only dependencies.',
      exclusions: 'Assignment routing logic belongs in the Assignment & Routing Pack. SLA policy changes belong in the SLA & Escalation Pack. AI prediction belongs in the AI & Prediction Pack. Data mandatory-field controls belong in the Business Rules & Data Quality Pack.'
    },
    {
      key: 'sn_assignment_routing',
      label: 'Assignment & Routing Pack',
      packTitle: 'ServiceNow Assignment & Routing Pack',
      progressTitle: 'Generating Assignment & Routing Pack',
      sublabel: 'Routing accuracy, ownership controls, reassignment loops and group handoff reduction',
      accent: '#3D6B54',
      capabilityFocus: 'Assignment Rules, Data Lookup, Decision Tables, Predictive Intelligence-assisted routing, Flow Designer routing steps, and group ownership controls',
      rulePrefix: 'sn-route',
      technicalRequirements: [
        { requirement: 'Assignment Rules, Data Lookup and Decision Tables are core platform — no additional licensing', type: 'Licensing' },
        { requirement: 'Predictive Intelligence-assisted routing requires the Predictive Intelligence plugin (ITSM Professional entitlement) — verify before recommending', type: 'Licensing' },
        { requirement: 'Advanced Work Assignment (AWA), if used for queue-based routing, requires ITSM Professional', type: 'Licensing' },
        { requirement: 'admin access to assignment rules and decision tables; validate routing changes in sub-production', type: 'Permission' },
      ],
      guidance: 'Generate routing-specific rules only: initial assignment, reassignment guardrails, assignment group churn reduction, dispatch queues, category/subcategory routing, CI/service-based routing, and ownership escalation.',
      exclusions: 'Do not include generic SLA warnings, Virtual Agent topics, dashboard ideas, or broad Flow Designer housekeeping unless they directly improve assignment/routing.'
    },
    {
      key: 'sn_sla_escalation',
      label: 'SLA & Escalation Pack',
      packTitle: 'ServiceNow SLA & Escalation Pack',
      progressTitle: 'Generating SLA & Escalation Pack',
      sublabel: 'SLA pause/resume logic, breach prevention, escalation and service-performance controls',
      accent: '#8F6A2A',
      capabilityFocus: 'SLA Definitions, Task SLA, Flow Designer escalation flows, notifications, scheduled jobs, breach warnings, pause/resume conditions, and Performance Analytics indicators',
      rulePrefix: 'sn-sla',
      technicalRequirements: [
        { requirement: 'SLA engine (SLA Definitions, Task SLA) is core ITSM — no additional licensing', type: 'Licensing' },
        { requirement: 'Performance Analytics indicators require a PA entitlement (bundled with ITSM Professional); native Reporting can substitute where PA is unavailable', type: 'Licensing' },
        { requirement: 'sla_admin / admin role; escalation flows are built in Flow Designer', type: 'Permission' },
        { requirement: 'SLA Definition changes affect in-flight Task SLA records — plan re-attachment and validate in sub-production', type: 'Deployment' },
      ],
      guidance: 'Generate SLA-specific controls only: breach risk warnings, pause/resume logic, elapsed percentage escalation, aged Task SLA follow-up, breach reporting, and SLA policy/data prerequisites.',
      exclusions: 'Do not include generic assignment routing or AI classification unless it directly changes SLA risk handling.'
    },
    {
      key: 'sn_business_controls',
      label: 'Business Rules & Data Quality Pack',
      packTitle: 'ServiceNow Business Rules & Data Quality Pack',
      progressTitle: 'Generating Business Rules & Data Quality Pack',
      sublabel: 'Mandatory fields, state discipline, closure quality, audit controls and data readiness',
      accent: '#7C3A3A',
      capabilityFocus: 'Business Rules, Client Scripts, UI Policies, Data Policies, dictionary configuration, validation scripts, audit/work-note controls, and reporting-quality safeguards',
      rulePrefix: 'sn-control',
      technicalRequirements: [
        { requirement: 'Business Rules, UI Policies, Data Policies and dictionary controls are core platform — no additional licensing', type: 'Licensing' },
        { requirement: 'admin (or scoped-app equivalent) access; track changes via update sets or application repository', type: 'Permission' },
        { requirement: 'Prefer server-side enforcement (before business rules / data policies) over client-only controls so API and integration writes are also covered', type: 'Design' },
        { requirement: 'Mandatory-field changes can break existing integrations and record producers — validate in sub-production', type: 'Deployment' },
      ],
      guidance: 'Generate governance and data-quality controls only: required fields at lifecycle gates, invalid state movement prevention, closure code discipline, work-note/comment requirements, category completeness, and data quality needed for automation or AI readiness.',
      exclusions: 'Do not include Flow Designer reminders, Virtual Agent topics, or predictive models unless the rule is a data prerequisite for those automations.'
    },
    {
      key: 'sn_ai_prediction',
      label: 'AI & Prediction Pack',
      packTitle: 'ServiceNow AI & Prediction Pack',
      progressTitle: 'Generating AI & Prediction Pack',
      sublabel: 'Predictive Intelligence, Now Assist, similarity, classification and AI-assisted decisions',
      accent: '#584A75',
      capabilityFocus: 'Predictive Intelligence, Now Assist-style summarisation or recommendation opportunities, classification models, similarity, clustering, suggested resolution, knowledge recommendation, and Performance Analytics signals',
      rulePrefix: 'sn-ai',
      technicalRequirements: [
        { requirement: 'Predictive Intelligence plugin requires ITSM Professional — verify entitlement before recommending', type: 'Licensing' },
        { requirement: 'Now Assist / generative AI capabilities are a separate SKU (typically Pro Plus) — do not assume availability', type: 'Licensing' },
        { requirement: 'Classification and similarity models need substantial resolved-record history with stable labels — confirm training data volume before committing', type: 'Data' },
        { requirement: 'admin / ml_admin role to train and publish models; validate predictions in sub-production before enabling auto-population', type: 'Deployment' },
      ],
      guidance: 'Generate only AI-suitable recommendations where the evidence suggests enough volume, stable historical labels, repeated patterns, or text/field signals. Include data prerequisites and a confidence/readiness rationale for each rule.',
      exclusions: 'Do not include simple deterministic Flow Designer, assignment, SLA or Business Rule controls unless explaining them as prerequisites for AI.'
    },
    {
      key: 'sn_self_service',
      label: 'Self-Service & Knowledge Pack',
      packTitle: 'ServiceNow Self-Service & Knowledge Pack',
      progressTitle: 'Generating Self-Service & Knowledge Pack',
      sublabel: 'Virtual Agent, Employee Center, catalog deflection and knowledge improvement opportunities',
      accent: '#3F5873',
      capabilityFocus: 'Virtual Agent, Employee Center, Service Portal, Knowledge, Catalog Items, Record Producers, Flow Designer fulfilment, and deflection analytics',
      rulePrefix: 'sn-ss',
      technicalRequirements: [
        { requirement: 'Service Catalog, Record Producers and Knowledge are core platform — no additional licensing', type: 'Licensing' },
        { requirement: 'Virtual Agent requires ITSM Professional — catalog and knowledge deflection work without it', type: 'Licensing' },
        { requirement: 'Employee Center Pro (targeted content, unified taxonomy) is a separate entitlement from base Employee Center', type: 'Licensing' },
        { requirement: 'catalog_admin / knowledge_admin roles; validate record producers and topics in sub-production', type: 'Permission' },
      ],
      guidance: 'Generate self-service recommendations only where the evidence suggests repeatable, high-volume, well-classified work, request-like incidents, recurring simple issues, knowledge reuse potential, or catalog deflection opportunities.',
      exclusions: 'Do not recommend Virtual Agent just because ServiceNow supports it. If the evidence does not show deflection potential, say readiness is Low and recommend data/knowledge prerequisites instead.'
    },
  ];

  const isServiceNowPackKey = (key) => serviceNowPackDefinitions.some(pack => pack.key === key);
  const getServiceNowPackDefinition = (key) => serviceNowPackDefinitions.find(pack => pack.key === key) || serviceNowPackDefinitions[0];

  const generateServiceNowAutomation = async (packDefinition = serviceNowPackDefinitions[0]) => {
    const pack = packDefinition || serviceNowPackDefinitions[0];
    stopAllAiProgressTimers();
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    processingCtr = 0;

    // Match the other Advisor AI generators: open the shared progress dialog first,
    // then let the AI timer update that dialog while Gemini is running.
    await openProgressModal({ status: pack.progressTitle, ai: true });
    aiProgressTimerRef.current = await startAiProgressTimer(pack.progressTitle);

    await sleep(SLEEP_DELAY_MS);
    const { issueTypeStr, metricsHistory, transitionsJSON, variantsJSON, sampleItems, fieldUsage, statusTypeSummary, activityType, reportScopeText } = getSharedData();
    const prompt = await injectOrganizationContext(`You are an expert ServiceNow architect and process-improvement consultant. Based on the process mining evidence below, generate an implementation-ready ${pack.packTitle}.

PLATFORM RULES
- This is ServiceNow, not Jira. Do not mention Jira Automation, A4J, Rovo, JMWE, JWT, ScriptRunner, Jira issues, or Atlassian.
- Use ServiceNow-native capabilities only.
- Primary capability focus for this pack: ${pack.capabilityFocus}.
- Use ServiceNow terms: task, incident, request, change, state, assignment group, assignee, priority, impact, urgency, SLA, work notes, comments, catalog task.
- Base every rule on named evidence from the payload: a state/field transition, rework pattern, slow variant, repeated handoff, assignment movement, SLA signal, field quality gap, or volume concentration.
- Do not provide generic recommendations. Each rule must be directly implementable and must include concrete configuration notes.
- Pack-specific guidance: ${pack.guidance}
- Pack exclusions: ${pack.exclusions || 'None'}
- The pack must not feel interchangeable with another pack. Each rule must clearly use the pack's primary capability and should avoid capabilities assigned to other packs.

OUTPUT FORMAT
Return only valid JSON with this shape:
{
  "version": "1.0.0",
  "source_system": "servicenow",
  "pack_type": "${pack.key}",
  "pack_title": "${pack.packTitle}",
  "readiness": "High | Medium | Low",
  "readiness_rationale": "<1 sentence>",
  "rules": [
    {
      "id": "${pack.rulePrefix}-001",
      "name": "<specific automation name>",
      "capability": "Flow Designer | Business Rule | Assignment Rule | Decision Table | SLA Definition | Virtual Agent | Predictive Intelligence | IntegrationHub | Performance Analytics | Knowledge | Catalog Item",
      "priority": "Critical | High | Medium",
      "setup_effort": "Low | Medium | High",
      "purpose": "<what this automation does and why, tied to the evidence>",
      "evidence": ["<specific evidence point from transitions/variants/field usage/sample tasks>", "<specific evidence point from transitions/variants/field usage/sample tasks>"],
      "why_this_pack": "<why this belongs in ${pack.label}, not another pack>",
      "trigger": "<ServiceNow trigger, e.g. incident created, state changes to X, assignment_group changes, SLA percentage reaches threshold>",
      "conditions": ["<condition 1>", "<condition 2>"],
      "actions": ["<action 1>", "<action 2>"],
      "implementation_label": "ServiceNow Configuration",
      "implementation_lang": "ServiceNow JSON",
      "implementation": {
        "target_table": "<incident | sc_req_item | sc_task | change_request | task | other>",
        "configuration_area": "<Flow Designer | Business Rule | Assignment Rule | SLA Definition | Virtual Agent | Predictive Intelligence | Performance Analytics | etc>",
        "trigger": "<configuration trigger>",
        "conditions": ["<configuration condition>"],
        "steps": ["<implementation step 1>", "<implementation step 2>"],
        "fields_used": ["<field name>", "<field name>"],
        "test_cases": ["<test case>"],
        "rollback_notes": ["<how to safely disable or revert>"]
      },
      "requirements": ["<permission / plugin / data requirement>"]
    }
  ]
}

RULE COUNT
Provide 4 to 6 rules for this pack, ordered by implementation priority. Do not generate generic platform inventory; generate only rules that are supported by the process evidence. If a pack has weak evidence, produce fewer high-quality rules and set readiness to Low or Medium rather than inventing generic automations.

${reportScopeText}
PROCESS EVIDENCE
Work type / table context: ${issueTypeStr}
Metrics history: ${JSON.stringify(metricsHistory)}
Transitions (${transitionsJSON.length}): ${JSON.stringify(transitionsJSON)}
Variants (${variantsJSON.length}): ${JSON.stringify(variantsJSON)}
Sample tasks: ${JSON.stringify(sampleItems)}
Field usage: ${JSON.stringify(fieldUsage)}
Activity context: ${JSON.stringify({ activity_field: activityType, activity_label: activityType })}
Activity type summary: ${JSON.stringify(statusTypeSummary)}`);
    try {
      const json = await fetchDirect(prompt);
      const normalisedJson = {
        ...json,
        source_system: 'servicenow',
        pack_type: json?.pack_type || pack.key,
        pack_title: json?.pack_title || pack.packTitle,
        pack_key: pack.key,
        technical_requirements: Array.isArray(json?.technical_requirements) && json.technical_requirements.length
          ? json.technical_requirements
          : pack.technicalRequirements,
      };
      setServiceNowPackData(prev => ({ ...prev, [pack.key]: normalisedJson }));
      setServiceNowGenerated(prev => ({ ...prev, [pack.key]: true }));
      setActiveView(pack.key);
    } catch (error) {
      console.error(`Error generating ${pack.packTitle}:`, error);
      alert(`Failed to generate ${pack.label}. ${error?.message || 'Please try again.'}`);
    } finally {
      stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      closeProgressModal();
    }
  };

  // ── Reset handler for regenerating packs ─────────────────────────────────
  const handleResetPack = () => {
    if (activeView === 'jira') {
      setJiraData(outputJSONFormat_Jira);
      setJiraGenerated(false);
    } else if (isServiceNowPackKey(activeView)) {
      setServiceNowPackData(prev => {
        const next = { ...prev };
        delete next[activeView];
        return next;
      });
      setServiceNowGenerated(prev => ({ ...prev, [activeView]: false }));
    } else if (activeView === 'rovo') {
      setRovoData(null);
      setRovoGenerated(false);
    } else if (activeView === 'scriptrunner') {
      setScriptRunnerData(createEmptyWorkflowPack('scriptrunner', 'ScriptRunner'));
      setScriptRunnerGenerated(false);
    } else if (activeView === 'jmwe') {
      setJMWE(createEmptyWorkflowPack('jmwe', 'JMWE'));
      setJMWEGenerated(false);
    } else if (activeView === 'jwt') {
      setJWT(createEmptyWorkflowPack('jwt', 'JWT'));
      setJWTGenerated(false);
    }
    setActiveView('grid');
  };

  // ── Derived state ─────────────────────────────────────────────────────────

  const isServiceNow = getAppVersion() === 'SNOW';
  const featuredEngines = isServiceNow
    ? serviceNowPackDefinitions.map(pack => ({
        key: pack.key,
        icon: null,
        label: pack.label,
        sublabel: pack.sublabel,
        generated: !!serviceNowGenerated[pack.key],
        generate: () => generateServiceNowAutomation(pack),
        accent: pack.accent,
      }))
    : [
        { key: 'jira',  icon: Jira_Icon,  label: 'Jira Automation', sublabel: 'Native A4J rules — importable JSON', generated: jiraGenerated,  generate: generateJira,  accent: '#0052CC' },
        { key: 'rovo',  icon: Rovo_Icon,  label: 'Rovo Agents',     sublabel: 'AI agent specs + JA pairing rules',  generated: rovoGenerated,  generate: generateRovo,  accent: '#0052CC' },
      ];
  const specialistEngines = isServiceNow
    ? []
    : [
        { key: 'scriptrunner', icon: ScriptRunner_Icon, label: 'ScriptRunner', sublabel: 'Cloud JS scripts',    generated: scriptRunnerGenerated, generate: generateScriptRunner, accent: '#0052CC' },
        { key: 'jmwe',         icon: JMWE_Icon,         label: 'JMWE',         sublabel: 'Workflow extensions', generated: JMWEGenerated,         generate: generateJMWE,         accent: '#0052CC' },
        { key: 'jwt',          icon: JWT_Icon,           label: 'JWT',          sublabel: 'Workflow toolbox',    generated: JWTGenerated,          generate: generateJWT,          accent: '#0052CC' },
      ];
  const allEngines = [...featuredEngines, ...specialistEngines];

  const activeEngine = allEngines.find(e => e.key === activeView);
  const activeIcon  = activeEngine?.icon  || null;
  const activeLabel = activeEngine ? `${activeEngine.label} — ${modelTitle}` : modelTitle;
  const activeAccent = activeEngine?.accent || '#0052CC';


  // Map of fix_theme -> [engine labels] across all generated Jira packs, so a
  // rule can flag that the same fix exists in another pack (deploy only one).
  const crossPackFixThemes = useMemo(() => {
    const packs = [
      ['Jira Automation', jiraGenerated ? jiraData : null],
      ['ScriptRunner',    scriptRunnerGenerated ? scriptRunnerData : null],
      ['JMWE',            JMWEGenerated ? JMWE : null],
      ['JWT',             JWTGenerated ? JWT : null],
    ];
    const map = {};
    packs.forEach(([label, data]) => {
      const ruleList = Array.isArray(data?.rules) ? data.rules
        : Array.isArray(data?.automation_opportunities) ? data.automation_opportunities : [];
      ruleList.forEach(r => {
        const theme = r?.fix_theme;
        if (!theme) return;
        (map[theme] = map[theme] || new Set()).add(label);
      });
    });
    return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v]]));
  }, [jiraData, scriptRunnerData, JMWE, JWT, jiraGenerated, scriptRunnerGenerated, JMWEGenerated, JWTGenerated]);

  const DATA_MAP = {
    jira: jiraData,
    rovo: rovoData,
    jmwe: JMWE,
    jwt: JWT,
    scriptrunner: scriptRunnerData,
    ...Object.fromEntries(serviceNowPackDefinitions.map(pack => [pack.key, serviceNowPackData[pack.key]])),
  };
  const getActiveExportData = () => DATA_MAP[exportType] || null;
  const getExportTitle = () => allEngines.find(e => e.key === exportType)?.label || 'Automation Pack';

  const buildAutomationPackExportHTML = (packData, packTitle) => {
    // Normalise rules for each engine — mirrors PackDashboard normalizers
    const engineKey = (() => {
      if (packData?.source_system === 'servicenow' || String(packData?.pack_title || '').startsWith('ServiceNow')) return 'servicenow';
      if (packData?.rules?.[0]?.rule_id?.startsWith('SR') || packData?.rules?.[0]?.script) return 'scriptrunner';
      if (packData?.pack_engine === 'jwt' || packData?.rules?.[0]?.jwt_actions || packData?.automation_opportunities) return 'jwt';
      if (packData?.rules?.[0]?.jmwe_actions) return 'jmwe';
      return 'jira';
    })();

    const getRules = () => {
      if (engineKey === 'jwt') return (packData?.rules || packData?.automation_opportunities || []);
      return (packData?.rules || []);
    };

    const normalise = (rule, idx) => {
      if (engineKey === 'scriptrunner') return {
        id: rule.rule_id || `SR-${idx+1}`,
        name: rule.name || 'Unnamed Rule',
        purpose: rule.intent || '',
        trigger: rule.trigger?.type + (rule.trigger?.schedule ? ` (${rule.trigger.schedule})` : ''),
        conditions: rule.when || [],
        actions: rule.actions || [],
        script: rule.script?.code || null,
        scriptLang: rule.script?.language || 'groovy',
        requirements: rule.risk_notes || [],
        fixTheme: rule.fix_theme || null,
      };
      if (engineKey === 'jmwe') return {
        id: rule.rule_id || `JMWE-${idx+1}`,
        name: rule.name || 'Unnamed Rule',
        purpose: rule.rationale || '',
        trigger: (rule.target_transitions||[]).map(t=>`${t.from} → ${t.to}`).join(', '),
        conditions: rule.preconditions?.jql ? [`JQL: ${rule.preconditions.jql}`] : [],
        actions: (rule.jmwe_actions||[]).map(a=>`${a.type}: ${a.name}`),
        script: (rule.jmwe_actions||[]).length ? JSON.stringify(rule.jmwe_actions, null, 2) : null,
        fixTheme: rule.fix_theme || null,
        scriptLang: 'JMWE JSON',
        requirements: rule.risks || [],
      };
      if (engineKey === 'jwt') {
        const actions = Array.isArray(rule.jwt_actions) ? rule.jwt_actions : [];
        const targetTransitions = Array.isArray(rule.target_transitions)
          ? rule.target_transitions.map(t => typeof t === 'string' ? t : `${t.from || '*'} → ${t.to || '*'}`)
          : [];
        return {
          id: rule.rule_id || rule.id || `JWT-${idx+1}`,
          name: rule.name || rule.title || 'Unnamed Rule',
          purpose: rule.rationale || rule.problem_statement || rule.goal || '',
          trigger: targetTransitions.join(', ') || rule.trigger || '',
          conditions: rule.preconditions?.jql ? [`JQL: ${rule.preconditions.jql}`] : (rule.preconditions?.notes ? [rule.preconditions.notes] : []),
          actions: actions.map(a => `${a.type || 'action'}: ${a.name || ''}`),
          script: actions.length ? JSON.stringify(actions, null, 2) : null,
          fixTheme: rule.fix_theme || null,
        scriptLang: 'JWT JSON',
          requirements: rule.risks || [],
        };
      }
      if (engineKey === 'servicenow') {
        const toList = (value) => {
          if (!value) return [];
          const arr = Array.isArray(value) ? value : [value];
          return arr.map(item => {
            if (item == null) return '';
            if (typeof item === 'string') return item;
            if (typeof item === 'number' || typeof item === 'boolean') return String(item);
            if (typeof item === 'object') {
              const preferred = ['requirement','condition','action','step','name','field','value','summary','description','test_case','rollback_note'];
              for (const key of preferred) {
                if (item[key] != null && item[key] !== '') return `${key.replace(/_/g, ' ')}: ${Array.isArray(item[key]) ? item[key].join(', ') : item[key]}`;
              }
              return Object.entries(item)
                .filter(([,v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length))
                .slice(0, 3)
                .map(([k,v]) => `${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.join(', ') : v}`)
                .join(' · ');
            }
            return '';
          }).filter(Boolean);
        };
        const impl = rule.implementation || rule.configuration || {};
        const implSections = [];
        if (impl.target_table) implSections.push({ label: 'Target table', values: [impl.target_table] });
        if (impl.configuration_area) implSections.push({ label: 'Configuration area', values: [impl.configuration_area] });
        if (impl.trigger) implSections.push({ label: 'Configuration trigger', values: [impl.trigger] });
        if (impl.conditions) implSections.push({ label: 'Configuration conditions', values: toList(impl.conditions) });
        if (impl.steps) implSections.push({ label: 'Implementation steps', values: toList(impl.steps) });
        if (impl.fields_used) implSections.push({ label: 'Fields used', values: toList(impl.fields_used) });
        if (impl.test_cases) implSections.push({ label: 'Test cases', values: toList(impl.test_cases) });
        if (impl.rollback_notes) implSections.push({ label: 'Rollback notes', values: toList(impl.rollback_notes) });
        return {
          id: rule.id || rule.rule_id || `SN-${idx+1}`,
          name: rule.name || rule.title || 'Unnamed ServiceNow Automation',
          purpose: rule.purpose || rule.description || rule.rationale || '',
          capability: rule.capability || '',
          priority: rule.priority || '',
          effort: rule.setup_effort || rule.effort || '',
          whyThisPack: rule.why_this_pack || '',
          evidence: toList(rule.evidence || rule.evidence_points || rule.evidence_signals),
          trigger: typeof rule.trigger === 'string' ? rule.trigger : toList(rule.trigger).join(', '),
          conditions: toList(rule.conditions || rule.preconditions),
          actions: toList(rule.actions || rule.flow_steps || rule.implementation_steps),
          implementationSections: implSections,
          requirements: toList(rule.requirements || rule.prerequisites),
        };
      }
      // jira
      return {
        id: rule.id || `RULE-${idx+1}`,
        name: rule.name || 'Unnamed Rule',
        purpose: rule.description || '',
        trigger: rule.triggers?.[0]?.type?.replace('jira.','').replace(/_/g,' ') || '',
        conditions: (rule.conditions||[]).map(c=>c.config?.queryString||c.type||''),
        actions: (rule.actions||[]).map(a=>{ if(typeof a==='string') return a; return (a.type||'').replace('jira.','').replace(/[._]/g,' '); }),
        script: rule.export?.a4j_cloud ? JSON.stringify(rule.export.a4j_cloud, null, 2) : null,
        scriptLang: 'Jira Automation JSON',
        requirements: rule.requirements || [],
        fixTheme: rule.fix_theme || null,
      };
    };

    const ENGINE_LABELS = { jira: 'Jira Automation', scriptrunner: 'ScriptRunner', jmwe: 'JMWE', jwt: 'JWT' };
    const rules = getRules().map(normalise).map(r => ({
      ...r,
      altEngines: r.fixTheme ? (crossPackFixThemes[r.fixTheme] || []).filter(l => l !== ENGINE_LABELS[engineKey]) : [],
    }));

    // Pack-level requirements
    const packReqs = {
      scriptrunner: ['ScriptRunner for Jira Cloud installed','Script Runner Admin permission','Scripts are Groovy using the built-in Unirest-style REST helpers (get/post/put) or HAPI','Deploy via ScriptRunner → Script Console, Script Listeners or Scheduled Jobs'],
      jmwe: ['JMWE for Cloud installed','Workflow Admin permission','Configure via Workflow editor → Transition → Post-functions / Validators'],
      jwt: ['Jira Workflow Toolbox (JWT) for Cloud installed','Workflow Admin permission','Configure via Workflow editor → JWT validators/conditions/post-functions'],
      jira: ['Jira Cloud with Automation for Jira enabled','Project Admin permission to import automation rules','Import via: Project Settings → Automation → Import rule'],
      servicenow: [
        ...(Array.isArray(packData?.technical_requirements) && packData.technical_requirements.length
          ? packData.technical_requirements.map(r => typeof r === 'string' ? r : [r.type, r.requirement].filter(Boolean).join(': '))
          : ['ServiceNow scoped application / admin access','Relevant ServiceNow product/plugins for the selected pack capability']),
        'Validate in sub-production before promoting through update sets or application repository',
        ...(packData?.readiness ? [`Automation Readiness: ${packData.readiness}${packData.readiness_rationale ? ' — ' + packData.readiness_rationale : ''}`] : []),
      ],
    }[engineKey] || [];

    const escHtml = s => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const dotList = arr => arr.length ? arr.map(x=>`<div class="dot-item"><span class="dot">·</span><span>${escHtml(x)}</span></div>`).join('') : '';

    const serviceNowSection = (title, items) => items?.length ? `
        <div class="detail-section">
          <div class="detail-title">${escHtml(title)}</div>
          <div class="detail-body">${dotList(items)}</div>
        </div>` : '';

    const implementationHtml = (rule) => {
      if (engineKey !== 'servicenow') return '';
      const sections = rule.implementationSections || [];
      if (!sections.length) return '';
      return `<div class="implementation-panel">
        <div class="implementation-title">ServiceNow implementation</div>
        ${sections.map(section => `
          <div class="impl-row">
            <div class="impl-label">${escHtml(section.label)}</div>
            <div class="impl-value">${dotList(section.values || [])}</div>
          </div>`).join('')}
      </div>`;
    };

    const metaHtml = (rule) => engineKey === 'servicenow' ? `
      <div class="meta-row">
        ${rule.capability ? `<span class="meta-pill">${escHtml(rule.capability)}</span>` : ''}
        ${rule.priority ? `<span class="meta-pill">Priority: ${escHtml(rule.priority)}</span>` : ''}
        ${rule.effort ? `<span class="meta-pill">Effort: ${escHtml(rule.effort)}</span>` : ''}
      </div>` : '';

    const rulesHtml = rules.map(rule => `
      <div class="rule-card">
        <div class="rule-header">
          <span class="rule-id">${escHtml(rule.id)}</span>
          <div class="rule-name">${escHtml(rule.name)}</div>
          ${metaHtml(rule)}
          ${rule.purpose ? `<div class="rule-purpose">${escHtml(rule.purpose)}</div>` : ''}
          ${rule.whyThisPack ? `<div class="why-pack"><strong>Why this pack:</strong> ${escHtml(rule.whyThisPack)}</div>` : ''}
          ${rule.altEngines?.length ? `<div style="margin-top:6px;font-size:11.5px;font-weight:600;color:#403294;background:#EAE6FF;border:1px solid #C0B6F2;border-radius:6px;padding:4px 10px;display:inline-block;">Alternative implementation of the same fix available in: ${escHtml(rule.altEngines.join(', '))} — deploy in one app only</div>` : ''}
        </div>
        <div class="rule-row">
          ${rule.trigger ? `<div class="rule-col rule-col-trigger"><div class="col-label">Trigger</div><div class="col-val">${escHtml(rule.trigger)}</div></div>` : ''}
          ${rule.conditions?.length ? `<div class="rule-col rule-col-mid"><div class="col-label">Conditions</div><div class="col-val">${dotList(rule.conditions)}</div></div>` : ''}
          ${rule.actions?.length ? `<div class="rule-col rule-col-last"><div class="col-label">Actions</div><div class="col-val">${dotList(rule.actions)}</div></div>` : ''}
        </div>
        ${engineKey === 'servicenow' ? serviceNowSection('Evidence used', rule.evidence) : ''}
        ${implementationHtml(rule)}
        ${engineKey === 'servicenow' ? serviceNowSection('Requirements / prerequisites', rule.requirements) : ''}
        ${engineKey !== 'servicenow' && rule.script ? `
        <details class="script-section" open>
          <summary>Script <span class="lang-badge">${escHtml(rule.scriptLang)}</span></summary>
          <pre class="code-block">${escHtml(rule.script)}</pre>
        </details>` : ''}
      </div>`).join('');

    const reqsHtml = packReqs.length ? `
      <div class="reqs-card">
        <div class="reqs-title">⚙ Technical Requirements</div>
        ${packReqs.map(r=>`<div class="req-item">${escHtml(r)}</div>`).join('')}
      </div>` : '';

    const safeTitle = escHtml(packTitle || 'Automation Pack');
    const model = escHtml(modelTitle || '');

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>${safeTitle}</title>
<style>
*{box-sizing:border-box;}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',sans-serif;background:#F4F5F7;color:#172B4D;margin:0;padding:20px;}
.wrap{max-width:1100px;margin:0 auto;}
.hero{background:#fff;border:1px solid #DFE1E6;border-radius:10px;padding:20px 24px;margin-bottom:12px;border-top:4px solid #0052CC;}
.hero-title{font-size:26px;font-weight:800;color:#172B4D;margin:0 0 4px;}
.hero-sub{font-size:13px;color:#7A869A;}
.engine-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin:12px 0 8px;}
.reqs-card{background:#fff;border:1px solid #DFE1E6;border-left:4px solid #974F0C;border-radius:8px;padding:12px 16px;margin-bottom:12px;}
.reqs-title{font-size:13px;font-weight:700;color:#172B4D;margin-bottom:8px;}
.req-item{font-size:12px;color:#42526E;padding:4px 0;border-bottom:1px solid #F4F5F7;}
.req-item:last-child{border-bottom:none;}
.rule-card{background:#fff;border:1px solid #DFE1E6;border-radius:10px;margin-bottom:10px;overflow:hidden;}
.rule-header{padding:14px 16px;background:linear-gradient(135deg,#00875A0A 0%,transparent 100%);border-bottom:1px solid #DFE1E6;}
.rule-id{display:inline-block;font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#00875A;background:#00875A12;padding:2px 8px;border-radius:4px;margin-bottom:6px;}
.rule-name{font-size:15px;font-weight:700;color:#172B4D;margin-bottom:4px;}
.rule-purpose{font-size:13px;color:#42526E;line-height:1.5;}
.meta-row{display:flex;gap:6px;flex-wrap:wrap;margin:6px 0 8px;}
.meta-pill{font-size:10px;font-weight:700;color:#0052CC;background:#0052CC12;border:1px solid #0052CC22;border-radius:999px;padding:2px 8px;}
.why-pack{font-size:12px;color:#42526E;background:#F4F5F7;border:1px solid #DFE1E6;border-radius:6px;padding:8px 10px;margin-top:8px;}
.rule-row{display:flex;border-top:1px solid #DFE1E6;}
.detail-section{padding:10px 14px;border-top:1px solid #DFE1E6;background:#FAFBFC;}
.detail-title,.implementation-title{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:6px;}
.detail-body{font-size:12px;color:#42526E;line-height:1.5;}
.implementation-panel{padding:12px 14px;border-top:1px solid #DFE1E6;background:#FFFFFF;}
.impl-row{display:grid;grid-template-columns:180px 1fr;gap:10px;padding:7px 0;border-bottom:1px solid #F4F5F7;}
.impl-row:last-child{border-bottom:none;}
.impl-label{font-size:11px;font-weight:700;color:#172B4D;}
.impl-value{font-size:12px;color:#42526E;line-height:1.5;}

.rule-col{padding:10px 14px;border-right:1px solid #DFE1E6;}
.rule-col:last-child{border-right:none;}
.rule-col-trigger{flex:0 0 auto;min-width:160px;max-width:240px;}
.rule-col-mid{flex:1;}
.rule-col-last{flex:1;}
.col-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
.col-val{font-size:12px;color:#42526E;line-height:1.5;}
.dot-item{display:flex;gap:6px;align-items:flex-start;margin-bottom:3px;}
.dot{color:#6B778C;flex-shrink:0;}
.script-section{border-top:1px solid #DFE1E6;}
.script-section summary{padding:10px 14px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#00875A;cursor:pointer;background:#00875A08;display:flex;align-items:center;gap:8px;}
.lang-badge{font-size:10px;font-weight:700;padding:2px 7px;border-radius:3px;background:#0052CC22;color:#0052CC;}
.code-block{margin:0;padding:14px 16px;background:#1e2432;color:#e8eeff;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:12px;line-height:1.65;overflow-x:auto;white-space:pre-wrap;word-break:break-word;max-height:400px;overflow-y:auto;}
@media(max-width:700px){.rule-row{flex-direction:column;}.rule-col{border-right:none;border-bottom:1px solid #DFE1E6;}.rule-col-trigger{max-width:none;}}
</style>
</head>
<body>
<div class="wrap">
  <div class="hero">
    <div class="hero-title">${safeTitle}</div>
    ${model ? `<div class="hero-sub">${model}</div>` : ''}
  </div>
  ${reqsHtml}
  <div class="engine-label">${escHtml(packTitle||'')} — ${rules.length} rule${rules.length!==1?'s':''}</div>
  ${rulesHtml}
</div>
</body>
</html>`;
  };


  const buildAutomationPackPptx = async (packData, packTitle, packKey, themeId = DEFAULT_REPORT_THEME) => {
    const { default: PptxGenJS } = await import('pptxgenjs');
    const pptx = new PptxGenJS();
    pptx.author = 'Metricus Process Advisor';
    pptx.company = 'Metricus';
    pptx.subject = packTitle || 'Automation Pack';
    pptx.title = `${packTitle || 'Automation Pack'} — ${modelTitle || 'Process Model'}`;
    pptx.lang = 'en-US';
    pptx.theme = { headFontFace: 'Aptos Display', bodyFontFace: 'Aptos', lang: 'en-US' };
    pptx.defineLayout({ name: 'CUSTOM_WIDE', width: 13.333, height: 7.5 });
    pptx.layout = 'CUSTOM_WIDE';

    const theme = REPORT_THEMES?.[themeId] || REPORT_THEMES?.[DEFAULT_REPORT_THEME] || {};
    const C = {
      primary: theme.pptx?.primary || '0052CC',
      navy: theme.pptx?.navy || '172B4D',
      primaryDark: theme.pptx?.primaryDark || '09326C',
      accent: theme.pptx?.accent || '1D7AFC',
      danger: theme.pptx?.danger || 'C9372C',
      dangerBg: theme.pptx?.dangerBg || 'FFECEB',
      warn: theme.pptx?.warn || 'A54800',
      warnBg: theme.pptx?.warnBg || 'FFF3EB',
      success: theme.pptx?.success || '216E4E',
      successBg: theme.pptx?.successBg || 'DCFFF1',
      ink: theme.pptx?.ink || '172B4D',
      text: theme.pptx?.text || '44546F',
      muted: theme.pptx?.muted || '626F86',
      card: theme.pptx?.card || 'FAFBFC',
      tint: theme.pptx?.tint || 'E9F2FF',
      border: theme.pptx?.border || 'E1E5EA',
      white: theme.pptx?.white || 'FFFFFF',
      darkCard: '1E2432',
    };

    const W = 13.333;
    const H = 7.5;
    const M = 0.42;
    const contentBottom = 6.86;
    const brandSuffix = theme.brandSuffix || (getAppVersion() === 'SNOW' ? 'for ServiceNow' : 'for Jira');

    const safe = (value, fallback = '') => {
      if (value == null) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (Array.isArray(value)) return value.map(v => safe(v)).filter(Boolean).join(', ');
      if (typeof value === 'object') {
        const preferred = ['name', 'title', 'summary', 'description', 'label', 'value', 'type', 'condition', 'action', 'step', 'field', 'body', 'queryString', 'jql', 'cron'];
        for (const k of preferred) if (value[k] != null && value[k] !== '') return safe(value[k]);
        return Object.entries(value).slice(0, 4).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${safe(v)}`).join(' · ');
      }
      return String(value || fallback);
    };
    const list = (value) => {
      if (!value) return [];
      const arr = Array.isArray(value) ? value : [value];
      return arr.map(v => safe(v)).map(v => String(v).trim()).filter(Boolean);
    };
    const trunc = (txt, max = 260) => {
      const t = safe(txt).replace(/\s+/g, ' ').trim();
      return t.length > max ? `${t.slice(0, Math.max(0, max - 1)).trim()}…` : t;
    };
    const titleCase = (txt) => String(txt || '').replace(/[._-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const statusColor = (value) => {
      const v = String(value || '').toLowerCase();
      if (v.includes('critical') || v.includes('high')) return { fg: C.danger, bg: C.dangerBg, border: 'FDA29B' };
      if (v.includes('medium') || v.includes('moderate')) return { fg: C.warn, bg: C.warnBg, border: 'FEDF89' };
      if (v.includes('low') || v.includes('ready')) return { fg: C.success, bg: C.successBg, border: '57D9A3' };
      return { fg: C.primary, bg: C.tint, border: C.border };
    };

    const addHeader = (slide, title, subtitle = '') => {
      slide.addText(`PROCESS ADVISOR · ${String(packTitle || 'AUTOMATION PACK').toUpperCase()}`, {
        x: M, y: 0.2, w: W - (M * 2), h: 0.16,
        fontSize: 7.5, bold: true, color: C.muted, margin: 0, breakLine: false,
      });
      slide.addText(title, {
        x: M, y: 0.47, w: W - (M * 2), h: 0.32,
        fontSize: 17, bold: true, color: C.navy, margin: 0, fit: 'shrink', breakLine: false,
      });
      if (subtitle) slide.addText(subtitle, {
        x: M, y: 0.84, w: W - (M * 2), h: 0.22,
        fontSize: 8.6, color: C.text, margin: 0, fit: 'shrink', breakLine: false,
      });
      slide.addShape(pptx.ShapeType.line, { x: M, y: 1.12, w: W - (M * 2), h: 0, line: { color: C.border, width: 1 } });
    };
    const addFooter = (slide, pageNum) => {
      slide.addText(`Process Advisor ${brandSuffix}  ·  Automation Pack  ·  ${modelTitle || 'Metricus'}`, {
        x: M, y: H - 0.28, w: 8.8, h: 0.14, fontSize: 6.8, color: C.muted, margin: 0,
      });
      slide.addText(String(pageNum), { x: W - 0.78, y: H - 0.28, w: 0.28, h: 0.14, fontSize: 6.8, color: C.muted, align: 'right', margin: 0 });
    };
    const addCard = (slide, x, y, w, h, opts = {}) => {
      slide.addShape(pptx.ShapeType.roundRect, {
        x, y, w, h, rectRadius: 0.06,
        fill: { color: opts.fill || C.white },
        line: { color: opts.line || C.border, width: opts.lineWidth || 0.7 },
      });
      if (opts.accent) slide.addShape(pptx.ShapeType.rect, { x, y, w: 0.06, h, fill: { color: opts.accent }, line: { color: opts.accent, transparency: 100 } });
    };
    const addLabel = (slide, label, x, y, w, color = C.primary) => {
      slide.addText(String(label || '').toUpperCase(), { x, y, w, h: 0.13, fontSize: 6.9, bold: true, color, margin: 0, breakLine: false, fit: 'shrink' });
    };
    const addBody = (slide, text, x, y, w, h, opts = {}) => {
      slide.addText(trunc(text, opts.max || 520), {
        x, y, w, h, fontSize: opts.fontSize || 8.2, color: opts.color || C.text,
        margin: opts.margin ?? 0.03, fit: 'shrink', valign: 'mid', breakLine: false,
      });
    };
    const addBullets = (slide, items, x, y, w, h, opts = {}) => {
      const lines = list(items).slice(0, opts.maxItems || 5);
      if (!lines.length) return;
      slide.addText(lines.map(item => ({ text: trunc(item, opts.maxChars || 150), options: { bullet: { indent: 9 }, hanging: 3 } })), {
        x, y, w, h, fontSize: opts.fontSize || 7.6, color: opts.color || C.text,
        margin: 0.03, fit: 'shrink', valign: 'mid', breakLine: false,
      });
    };
    const addPill = (slide, text, x, y, w, opts = {}) => {
      const c = opts.colors || statusColor(text);
      slide.addShape(pptx.ShapeType.roundRect, { x, y, w, h: 0.22, rectRadius: 0.08, fill: { color: c.bg }, line: { color: c.border || c.fg, width: 0.6 } });
      slide.addText(trunc(text, opts.max || 34), { x: x + 0.04, y: y + 0.055, w: w - 0.08, h: 0.08, fontSize: opts.fontSize || 6.6, bold: true, color: c.fg, align: 'center', margin: 0, fit: 'shrink', breakLine: false });
    };
    const addKeyValueRows = (slide, rows, x, y, w, h, opts = {}) => {
      const cleanRows = rows.filter(r => r && r[1]);
      if (!cleanRows.length) return;
      const rowH = Math.min(0.43, h / cleanRows.length);
      cleanRows.slice(0, Math.floor(h / 0.28)).forEach(([label, value], idx) => {
        const yy = y + idx * rowH;
        slide.addText(label, { x, y: yy + 0.03, w: opts.labelW || 1.28, h: 0.12, fontSize: opts.labelSize || 6.5, bold: true, color: opts.labelColor || C.muted, margin: 0, fit: 'shrink', breakLine: false });
        addBody(slide, value, x + (opts.labelW || 1.36), yy, w - (opts.labelW || 1.36), rowH - 0.02, { max: opts.max || 150, fontSize: opts.fontSize || 7.2 });
        if (idx < cleanRows.length - 1) slide.addShape(pptx.ShapeType.line, { x, y: yy + rowH - 0.02, w, h: 0, line: { color: C.border, width: 0.35, transparency: 30 } });
      });
    };

    const getEngineKey = () => {
      if (packKey === 'rovo') return 'rovo';
      if (packData?.source_system === 'servicenow' || String(packData?.pack_title || '').startsWith('ServiceNow')) return 'servicenow';
      if (packKey === 'scriptrunner' || packData?.rules?.[0]?.rule_id?.startsWith('SR') || packData?.rules?.[0]?.script) return 'scriptrunner';
      if (packKey === 'jwt' || packData?.pack_engine === 'jwt' || packData?.rules?.[0]?.jwt_actions || packData?.automation_opportunities) return 'jwt';
      if (packKey === 'jmwe' || packData?.rules?.[0]?.jmwe_actions) return 'jmwe';
      return 'jira';
    };
    const engineKey = getEngineKey();
    const isJwtPpt = false;

    // JWT implementation packs can be text-heavy. Keep the PPT readable by
    // using larger fonts and slightly larger cards for JWT only. Other
    // automation-pack exporters intentionally retain their existing density.
    const jwtText = {
      overviewBody: 8.0,
      overviewMeta: 7.3,
      overviewTitle: 9.0,
      detailBody: 9.4,
      detailSmall: 8.2,
      detailBullet: 8.1,
      implBody: 9.0,
      implBullet: 8.2,
      implRow: 8.0,
      implLabel: 7.2,
      note: 7.2,
    };

    const describeAction = (action) => {
      if (!action) return '';
      if (typeof action === 'string') return titleCase(action);
      const type = titleCase(action.type || action.component || 'Action');
      const cfg = action.config || action.value || {};
      const details = [];
      if (cfg.transition_name) details.push(`transition: ${cfg.transition_name}`);
      if (cfg.transition?.name) details.push(`transition: ${cfg.transition.name}`);
      if (cfg.transition?.value) details.push(`transition: ${cfg.transition.value}`);
      if (cfg.comment) details.push(`comment: ${safe(cfg.comment?.body || cfg.comment?.value || cfg.comment)}`);
      if (cfg.fields) details.push(`fields: ${Object.keys(cfg.fields).join(', ')}`);
      if (cfg.operations) details.push(`fields: ${cfg.operations.map(o => safe(o.field)).filter(Boolean).join(', ')}`);
      if (cfg.field) details.push(`${safe(cfg.field)} = ${safe(cfg.value)}`);
      if (cfg.url) details.push(`webhook: ${cfg.url}`);
      return [type, ...details].filter(Boolean).join(' — ');
    };
    const describeTrigger = (trigger) => {
      if (!trigger) return '';
      if (typeof trigger === 'string') return titleCase(trigger);
      const cfg = trigger.config || trigger.value || {};
      const type = titleCase(trigger.type || trigger.component || 'Trigger');
      const bits = [];
      if (cfg.jql) bits.push(`JQL: ${cfg.jql}`);
      if (cfg.queryString) bits.push(`JQL: ${cfg.queryString}`);
      if (cfg.cron) bits.push(`Cron: ${cfg.cron}`);
      if (cfg.schedule?.cron) bits.push(`Cron: ${cfg.schedule.cron}`);
      if (cfg.from_status || cfg.to_status) bits.push(`${cfg.from_status || '*'} → ${cfg.to_status || '*'}`);
      return [type, ...bits].filter(Boolean).join(' · ');
    };
    const findExportObj = (rule) => rule?.export?.a4j_cloud || rule?.jira_automation_json || rule?.export || null;
    const compactJsonRows = (obj) => {
      if (!obj || typeof obj !== 'object') return [];
      const trigger = obj.trigger || {};
      const value = trigger.value || {};
      const components = Array.isArray(obj.components) ? obj.components : [];
      return [
        ['Name', obj.name],
        ['State', obj.state],
        ['Trigger', titleCase(trigger.type || trigger.component)],
        ['Schedule', safe(value.schedule?.cron || value.cron || value.schedule)],
        ['JQL', safe(value.jql || value.queryString)],
        ['Actions', components.map(c => titleCase(c.type || c.component)).filter(Boolean).join(', ')],
      ];
    };

    const normaliseAutomationItems = () => {
      if (engineKey === 'rovo') {
        return (packData?.agents || []).map((a, idx) => ({
          id: `Agent ${idx + 1}`,
          name: a.name || 'Unnamed Rovo Agent',
          product: a.rovo_product || 'Rovo Agents',
          priority: a.priority || '',
          setup: a.setup_effort || '',
          purpose: a.purpose || a.evidence_signal || a.expected_benefit || '',
          evidence: a.evidence_signal || '',
          trigger: a.trigger || '',
          conditions: list(a.prerequisites),
          actions: list(a.actions),
          benefit: a.expected_benefit || '',
          pairing: a.jira_automation_pairing || '',
          instructions: a.agent_instructions || '',
          exportObj: a.jira_automation_json || null,
          meta: [a.rovo_product, a.priority ? `Priority: ${a.priority}` : '', a.setup_effort ? `Setup: ${a.setup_effort}` : '', a.requires_studio ? 'Rovo Studio' : ''].filter(Boolean),
        }));
      }

      const rawRules = engineKey === 'jwt' ? (packData?.rules || packData?.automation_opportunities || []) : (packData?.rules || []);
      return rawRules.map((rule, idx) => {
        if (engineKey === 'scriptrunner') {
          const triggerBits = [rule.trigger?.type, rule.trigger?.schedule ? `Schedule: ${rule.trigger.schedule}` : ''].filter(Boolean);
          const refBits = [
            ...(list(rule.references?.transitions).map(v => `Transition: ${v}`)),
            ...(list(rule.references?.variants).map(v => `Variant: ${v}`)),
          ];
          return {
            id: rule.rule_id || `SR-${idx + 1}`,
            name: rule.name || 'Unnamed ScriptRunner Rule',
            product: 'ScriptRunner',
            priority: rule.priority || '',
            setup: rule.effort || '',
            purpose: rule.intent || rule.description || '',
            trigger: triggerBits.join(' · '),
            schedule: rule.trigger?.schedule || '',
            jql: rule.jql_scope || '',
            when: list(rule.when),
            conditions: list(rule.when),
            actions: list(rule.actions),
            benefit: list(rule.telemetry).join(' '),
            evidence: list(rule.telemetry).join(' '),
            requirements: list(rule.risk_notes || rule.requirements),
            instructions: rule.script?.code || '',
            scriptPseudo: rule.script?.pseudo || '',
            scriptLang: rule.script?.language || 'groovy',
            jql: rule.jql_scope || '',
            implementationRows: [
              ['Rule ID', rule.rule_id],
              ['Script type', rule.trigger?.type],
              ['Schedule', rule.trigger?.schedule],
              ['JQL scope', rule.jql_scope],
              ['Language', rule.script?.language || 'groovy'],
              ['Pseudo logic', rule.script?.pseudo],
            ],
            validationPoints: [...list(rule.when), ...list(rule.actions), ...list(rule.telemetry), ...refBits],
            meta: ['ScriptRunner', rule.priority, rule.effort].filter(Boolean),
          };
        }
        if (engineKey === 'jmwe') {
          const rawTargetTransitions = Array.isArray(rule.target_transitions) ? rule.target_transitions : [];
          const targetTransitions = rawTargetTransitions.map(t => typeof t === 'string' ? t : `${t.from || '*'} → ${t.to || '*'}`);
          const applies = [
            ...(list(rule.applies_to?.project_keys).map(v => `Project: ${v}`)),
            ...(list(rule.applies_to?.issue_types).map(v => `Issue type: ${v}`)),
            ...(list(rule.applies_to?.workflow_names).map(v => `Workflow: ${v}`)),
          ];
          const preconditions = [rule.preconditions?.jql ? `JQL: ${rule.preconditions.jql}` : '', rule.preconditions?.notes || '', ...applies].filter(Boolean);
          const rawActions = Array.isArray(rule.jmwe_actions) ? rule.jmwe_actions : (Array.isArray(rule.actions) ? rule.actions : []);
          const actionLabels = rawActions.map(a => {
            const type = titleCase(a?.type || 'Action');
            const name = a?.name || safe(a);
            return `${type}: ${name}`;
          });
          const actionConfigRows = rawActions.map(a => {
            const cfg = a?.config || {};
            const cfgText = Object.entries(cfg).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${safe(v)}`).join(' · ');
            return [a?.name || titleCase(a?.type), cfgText || titleCase(a?.type)];
          });
          return {
            id: rule.rule_id || `JMWE-${idx + 1}`,
            name: rule.name || 'Unnamed JMWE Rule',
            product: 'JMWE',
            priority: rule.priority || '',
            setup: rule.effort || '',
            purpose: rule.rationale || rule.intent || rule.description || '',
            trigger: targetTransitions.join(', ') || safe(rule.trigger),
            jql: rule.preconditions?.jql || '',
            when: preconditions,
            conditions: preconditions,
            actions: actionLabels,
            benefit: list(rule.success_criteria).join(' '),
            evidence: rule.rationale || '',
            requirements: list(rule.risks),
            instructions: actionConfigRows.map(([k,v]) => `${k}: ${v}`).join('\n'),
            implementationRows: [
              ['Rule ID', rule.rule_id],
              ['Priority', rule.priority],
              ['Target transition', targetTransitions.join(', ')],
              ['Precondition', preconditions.join(' · ')],
              ['JMWE actions', actionLabels.join(', ')],
            ],
            validationPoints: [...list(rule.success_criteria), ...list(rule.risks), ...list(rule.references?.transition_indices).map(v => `Transition index: ${v}`), ...list(rule.references?.variant_indices).map(v => `Variant index: ${v}`)],
            actionConfigRows,
            meta: ['JMWE', rule.priority, rule.effort].filter(Boolean),
          };
        }
        if (engineKey === 'jwt') {
          const rawTargetTransitions = Array.isArray(rule.target_transitions) ? rule.target_transitions : [];
          const targetTransitions = rawTargetTransitions.map(t => typeof t === 'string' ? t : `${t.from || '*'} → ${t.to || '*'}`);
          const applies = [
            ...(list(rule.applies_to?.project_keys).map(v => `Project: ${v}`)),
            ...(list(rule.applies_to?.issue_types).map(v => `Issue type: ${v}`)),
            ...(list(rule.applies_to?.workflow_names).map(v => `Workflow: ${v}`)),
          ];
          const preconditions = [rule.preconditions?.jql ? `JQL: ${rule.preconditions.jql}` : '', rule.preconditions?.notes || '', ...applies].filter(Boolean);
          const rawActions = Array.isArray(rule.jwt_actions) ? rule.jwt_actions : [];
          const actionLabels = rawActions.map(a => {
            const type = titleCase(a?.type || 'Action');
            const name = a?.name || safe(a);
            return `${type}: ${name}`;
          });
          const actionConfigRows = rawActions.map(a => {
            const cfg = a?.config || {};
            const cfgText = Object.entries(cfg).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${safe(v)}`).join(' · ');
            return [a?.name || titleCase(a?.type), cfgText || titleCase(a?.type)];
          });
          return {
            id: rule.rule_id || rule.id || `JWT-${idx + 1}`,
            name: rule.name || rule.title || 'Unnamed JWT Rule',
            product: 'JWT',
            priority: rule.priority || '',
            setup: rule.effort || '',
            purpose: rule.rationale || rule.intent || rule.problem_statement || rule.goal || rule.description || '',
            trigger: targetTransitions.join(', ') || safe(rule.trigger),
            jql: rule.preconditions?.jql || '',
            when: preconditions,
            conditions: preconditions,
            actions: actionLabels,
            benefit: list(rule.success_criteria).join(' '),
            evidence: rule.rationale || '',
            requirements: list(rule.risks),
            instructions: actionConfigRows.map(([k,v]) => `${k}: ${v}`).join('\n'),
            implementationRows: [
              ['Rule ID', rule.rule_id || rule.id],
              ['Priority', rule.priority],
              ['Target transition', targetTransitions.join(', ')],
              ['Precondition', preconditions.join(' · ')],
              ['JWT actions', actionLabels.join(', ')],
            ],
            validationPoints: [...list(rule.success_criteria), ...list(rule.risks), ...list(rule.references?.transitions).map(v => `Transition: ${v}`), ...list(rule.references?.variants).map(v => `Variant: ${v}`)],
            actionConfigRows,
            meta: ['JWT', rule.priority, rule.effort].filter(Boolean),
          };
        }

        const exportObj = findExportObj(rule);
        return {
          id: rule.id || `RULE-${idx + 1}`,
          name: rule.name || 'Unnamed Jira Automation Rule',
          product: 'Jira Automation',
          priority: rule.risk_level ? `Risk: ${rule.risk_level}` : (rule.priority || ''),
          setup: rule.effort || '',
          purpose: rule.description || rule.purpose || '',
          trigger: describeTrigger(rule.triggers?.[0] || rule.trigger || exportObj?.trigger),
          conditions: list(rule.conditions).map(c => c?.config?.queryString || c?.value?.queryString || c?.type || safe(c)),
          actions: list(rule.actions).map(describeAction),
          benefit: rule.expected_benefit || rule.evidence || '',
          requirements: list(rule.requirements || rule.risks),
          instructions: '',
          exportObj,
          meta: ['Jira Automation', rule.risk_level ? `Risk: ${rule.risk_level}` : '', rule.priority, rule.effort].filter(Boolean),
        };
      });
    };

    const items = normaliseAutomationItems();
    const defaultJiraReqs = engineKey === 'jira'
      ? ['Jira Cloud with Automation for Jira enabled', 'Project Admin permission to import automation rules', 'Import via Project Settings → Automation → Import rule']
      : [];
    const packReqs = list(packData?.technical_requirements || packData?.requirements || packData?.prerequisites || defaultJiraReqs);
    const deckTitle = engineKey === 'rovo' ? (packData?.pack_title || 'Rovo Agent Pack') : (packTitle || packData?.pack_title || 'Automation Pack');
    const deckSubtitle = engineKey === 'rovo'
      ? (packData?.workflow_context || modelTitle || '')
      : `${items.length} rule${items.length === 1 ? '' : 's'} · ${modelTitle || 'Process model'}`;
    const itemLabel = engineKey === 'rovo' ? 'AGENTS' : 'RULES';
    const rovoPhases = (() => {
      const raw = packData?.implementation_sequence;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'object') return Object.values(raw).map((phase, idx) => ({
        phase: phase?.label || phase?.phase || phase?.name || `Phase ${idx + 1}`,
        description: phase?.description || phase?.summary || phase?.objective || '',
        agents: phase?.agents || [],
      }));
      return [];
    })();

    const prettyDate = () => new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const addMetricusLockup = (slide, x = 0.5, y = 0.54) => {
      try {
        const logoSource = typeof MetricusLogo === 'string' ? MetricusLogo : '';
        if (logoSource) {
          const imageInput = logoSource.startsWith('data:')
            ? { data: logoSource }
            : { path: logoSource };
          slide.addImage({ ...imageInput, x, y, w: 0.48, h: 0.48 });
        }
      } catch (e) {
        /* logo is optional if the image cannot be resolved by the host */
      }
      slide.addText('Process Advisor', { x: x + 0.58, y: y + 0.16, w: 1.72, h: 0.18, fontSize: 13.5, bold: true, color: C.white, margin: 0, breakLine: false, fit: 'shrink' });
      slide.addText(brandSuffix, { x: x + 2.36, y: y + 0.16, w: 1.5, h: 0.18, fontSize: 13.5, bold: true, color: '579DFF', margin: 0, breakLine: false, fit: 'shrink' });
    };
    const addSubtleDots = (slide, x0, y0, cols, rows, color = '77A6D8') => {
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          slide.addShape(pptx.ShapeType.ellipse, { x: x0 + c * 0.22, y: y0 + r * 0.22, w: 0.018, h: 0.018, fill: { color, transparency: 45 }, line: { color, transparency: 100 } });
        }
      }
    };
    const addPremiumCover = (slide, title, subtitle, count, countLabel) => {
      slide.background = { color: '08224A' };
      slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: W, h: H, fill: { color: '08224A' }, line: { color: '08224A' } });
      slide.addShape(pptx.ShapeType.rect, { x: 5.1, y: 0, w: W - 5.1, h: H, fill: { color: '0B3A72', transparency: 15 }, line: { color: '0B3A72', transparency: 100 } });
      slide.addShape(pptx.ShapeType.ellipse, { x: 7.2, y: 1.25, w: 8.6, h: 8.6, fill: { color: '08224A', transparency: 100 }, line: { color: '6FA8DC', transparency: 82, width: 1 } });
      slide.addShape(pptx.ShapeType.ellipse, { x: 8.05, y: 2.1, w: 6.9, h: 6.9, fill: { color: '08224A', transparency: 100 }, line: { color: '6FA8DC', transparency: 88, width: 1 } });
      addSubtleDots(slide, 10.4, 0.2, 9, 6);
      addSubtleDots(slide, 0.2, 6.22, 11, 5);
      addMetricusLockup(slide, 0.5, 0.55);
      slide.addText(title || 'JWT', { x: 0.5, y: 2.02, w: 6.3, h: 0.48, fontSize: 29, bold: true, color: C.white, margin: 0, fit: 'shrink', breakLine: false });
      slide.addShape(pptx.ShapeType.rect, { x: 0.5, y: 3.04, w: 1.18, h: 0.07, fill: { color: '579DFF' }, line: { color: '579DFF', transparency: 100 } });
      slide.addText(subtitle || modelTitle || '', { x: 0.5, y: 3.52, w: 6.8, h: 0.28, fontSize: 18, color: 'CFE1F6', margin: 0, fit: 'shrink', breakLine: false });
      slide.addShape(pptx.ShapeType.roundRect, { x: 9.48, y: 1.72, w: 3.55, h: 4.1, rectRadius: 0.08, fill: { color: C.white }, line: { color: 'DDE8F5', width: 1 } });
      slide.addText(String(count || 0), { x: 9.48, y: 2.88, w: 3.55, h: 0.78, fontSize: 44, bold: true, color: '13294B', align: 'center', margin: 0, breakLine: false });
      slide.addText(String(countLabel || 'RULES').toUpperCase(), { x: 9.48, y: 3.84, w: 3.55, h: 0.18, fontSize: 9.8, bold: true, color: '5E6C84', charSpace: 3, align: 'center', margin: 0, breakLine: false });
      slide.addText(`Prepared for Metricus   ·   ${prettyDate()}`, { x: 0.5, y: 6.88, w: 5.0, h: 0.16, fontSize: 8.8, color: '8FB2DA', margin: 0, breakLine: false });
    };
    const addSimpleSectionLabel = (slide, label, x, y, w = 2.1, color = C.primaryDark) => {
      slide.addText(String(label || '').toUpperCase(), { x, y, w, h: 0.12, fontSize: 7.5, bold: true, color, margin: 0, breakLine: false, fit: 'shrink' });
    };
    const addSimpleValue = (slide, value, x, y, w, h, opts = {}) => {
      addBody(slide, value, x, y, w, h, { max: opts.max || 360, fontSize: opts.fontSize || 9.0, color: opts.color || C.text });
    };

    if (['scriptrunner', 'jmwe', 'jwt'].includes(engineKey)) {
      const styleEngineName = packTitle || deckTitle || 'Automation Pack';
      const styleSubtitle = modelTitle || deckSubtitle || '';
      const pageBg = 'F7FAFF';
      const cardShadow = 'D7E3F3';
      const accentBlue = '1D7AFC';
      const lightBlue = 'E9F2FF';
      const green = '216E4E';
      const amber = 'A54800';

      const addStyledHeader = (slide, title, subtitle = '') => {
        slide.background = { color: pageBg };
        slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: W, h: H, fill: { color: pageBg }, line: { color: pageBg } });
        addSubtleDots(slide, 10.25, 0.2, 12, 8, 'A8C8F0');
        slide.addText(`PROCESS ADVISOR · ${String(styleEngineName).toUpperCase()}`, {
          x: M, y: 0.46, w: W - (M * 2), h: 0.16,
          fontSize: 8.2, bold: true, color: C.primary, charSpace: 2.8, margin: 0, breakLine: false,
        });
        slide.addText(title, {
          x: M, y: 0.74, w: W - (M * 2), h: 0.36,
          fontSize: 18.5, bold: true, color: C.navy, margin: 0, fit: 'shrink', breakLine: false,
        });
        slide.addShape(pptx.ShapeType.rect, { x: M, y: 1.21, w: 1.08, h: 0.05, fill: { color: accentBlue }, line: { color: accentBlue, transparency: 100 } });
        if (subtitle) slide.addText(subtitle, {
          x: M, y: 1.43, w: W - (M * 2), h: 0.18,
          fontSize: 8.8, bold: true, color: C.primary, margin: 0, fit: 'shrink', breakLine: false,
        });
      };
      const addStyledFooter = (slide, pageNum) => {
        slide.addText(`Process Advisor ${brandSuffix}  ·  ${styleEngineName}  ·  ${styleSubtitle || 'Metricus'}`, {
          x: M + 0.12, y: H - 0.29, w: 8.8, h: 0.14, fontSize: 7.0, color: C.muted, margin: 0,
        });
        slide.addText(String(pageNum), { x: W - 0.78, y: H - 0.29, w: 0.28, h: 0.14, fontSize: 7.0, color: C.muted, align: 'right', margin: 0 });
      };
      const addStyledCard = (slide, x, y, w, h, opts = {}) => {
        slide.addShape(pptx.ShapeType.roundRect, {
          x: x + 0.025, y: y + 0.035, w, h, rectRadius: 0.08,
          fill: { color: cardShadow, transparency: 72 },
          line: { color: cardShadow, transparency: 100 },
        });
        slide.addShape(pptx.ShapeType.roundRect, {
          x, y, w, h, rectRadius: 0.08,
          fill: { color: opts.fill || C.white },
          line: { color: opts.line || C.border, width: opts.lineWidth || 0.8 },
        });
        if (opts.accent) {
          slide.addShape(pptx.ShapeType.rect, { x, y: y + 0.08, w: 0.045, h: Math.max(0.12, h - 0.16), fill: { color: opts.accent }, line: { color: opts.accent, transparency: 100 } });
        }
      };
      const addCardTitle = (slide, text, x, y, w, color = C.navy) => {
        slide.addText(trunc(text, 95), { x, y, w, h: 0.18, fontSize: 10.2, bold: true, color, margin: 0, fit: 'shrink', breakLine: false });
      };
      const addStyledLabel = (slide, label, x, y, w, color = C.primary) => {
        slide.addText(String(label || '').toUpperCase(), { x, y, w, h: 0.12, fontSize: 7.4, bold: true, color, charSpace: 1.7, margin: 0, breakLine: false, fit: 'shrink' });
      };

      let page = 1;
      const cover = pptx.addSlide();
      addPremiumCover(cover, styleEngineName, styleSubtitle, items.length, 'Rules');

      const overview = pptx.addSlide(); page += 1;
      addStyledHeader(overview, '1. Pack Overview', '');
      const cols = 2;
      const cardW = (W - (M * 2) - 0.22) / 2;
      const cardH = 0.82;
      const startY = 1.55;
      items.slice(0, 6).forEach((r, idx) => {
        const col = idx % cols;
        const row = Math.floor(idx / cols);
        const x = M + col * (cardW + 0.22);
        const y = startY + row * (cardH + 0.22);
        addStyledCard(overview, x, y, cardW, cardH, { accent: accentBlue });
        addCardTitle(overview, `${idx + 1}. ${r.name || r.id}`, x + 0.16, y + 0.16, cardW - 0.28);
        addBody(overview, r.purpose || r.evidence || r.benefit || '', x + 0.16, y + 0.42, cardW - 0.28, 0.22, { max: 145, fontSize: 8.2, color: C.text });
      });
      addStyledFooter(overview, page);

      items.forEach((r, idx) => {
        const s = pptx.addSlide(); page += 1;
        addStyledHeader(s, `${idx + 2}. ${r.name || r.id}`, styleEngineName);

        addStyledCard(s, M, 1.68, W - (M * 2), 0.92, { accent: accentBlue, fill: lightBlue, line: '7AB2FF' });
        addStyledLabel(s, 'Rule intent', M + 0.18, 1.9, 1.8, C.primary);
        addBody(s, r.purpose || r.evidence || '', M + 0.18, 2.12, W - (M * 2) - 0.36, 0.24, { max: 350, fontSize: 8.9, color: C.text });

        addStyledCard(s, M, 2.78, W - (M * 2), 0.58, { accent: accentBlue });
        addStyledLabel(s, 'Trigger', M + 0.18, 3.02, 1.2, C.primaryDark);
        addBody(s, r.trigger || `${styleEngineName} workflow control`, M + 1.55, 2.99, 5.6, 0.18, { max: 120, fontSize: 8.8, color: C.navy });
        if (r.schedule) {
          addStyledLabel(s, 'Schedule', M + 7.12, 3.02, 1.25, C.primaryDark);
          addBody(s, r.schedule, M + 8.3, 2.99, W - M - 8.45, 0.18, { max: 90, fontSize: 8.5, color: C.navy });
        } else if (r.jql) {
          addStyledLabel(s, 'JQL scope', M + 7.12, 3.02, 1.25, C.primaryDark);
          addBody(s, r.jql, M + 8.3, 2.99, W - M - 8.45, 0.18, { max: 90, fontSize: 8.1, color: C.text });
        }

        const leftX = M;
        const rightX = 6.82;
        const panelY = 3.58;
        const panelH = 2.78;
        const panelW = 6.15;
        addStyledCard(s, leftX, panelY, panelW, panelH, { accent: green });
        addStyledLabel(s, 'Actions', leftX + 0.18, panelY + 0.22, 1.4, green);
        addBullets(s, r.actions || [], leftX + 0.18, panelY + 0.55, panelW - 0.36, 0.95, { maxItems: 4, fontSize: 8.5, maxChars: 110, color: C.text });
        addStyledLabel(s, 'Expected benefit', leftX + 0.18, panelY + 1.72, 2.2, green);
        addBody(s, r.benefit || r.evidence || 'Evidence-backed automation opportunity generated from process analysis.', leftX + 0.18, panelY + 1.98, panelW - 0.36, 0.42, { max: 210, fontSize: 8.3, color: C.text });

        addStyledCard(s, rightX, panelY, W - M - rightX, panelH, { accent: amber });
        addStyledLabel(s, 'Conditions', rightX + 0.18, panelY + 0.22, 1.6, amber);
        addBullets(s, r.when?.length ? r.when : (r.conditions || []), rightX + 0.18, panelY + 0.55, W - M - rightX - 0.36, 0.88, { maxItems: 3, fontSize: 8.3, maxChars: 110, color: C.text });
        addStyledLabel(s, 'Implementation logic', rightX + 0.18, panelY + 1.72, 2.6, amber);
        addBody(s, r.scriptPseudo || r.instructions || `Configure the ${styleEngineName} validator, condition, post-function, listener or scheduled rule described above.`, rightX + 0.18, panelY + 1.98, W - M - rightX - 0.36, 0.46, { max: 220, fontSize: 8.2, color: C.text });
        addBody(s, `Full copy-ready ${styleEngineName} configuration is in the JSON/HTML export.`, rightX + 0.18, panelY + 2.5, W - M - rightX - 0.36, 0.12, { max: 120, fontSize: 7.4, color: C.muted });
        addStyledFooter(s, page);
      });

      const final = pptx.addSlide(); page += 1;
      addStyledHeader(final, `${page}. Implementation Checklist`, '');
      addStyledCard(final, M, 1.55, W - (M * 2), 1.1, { accent: accentBlue, fill: lightBlue, line: '7AB2FF' });
      addStyledLabel(final, 'How to use this pack', M + 0.18, 1.88, 2.2, C.primary);
      addBody(final, `This deck summarises the generated ${styleEngineName} content in a client-readable format. Use the JSON/HTML export for the full copy-ready configuration artifacts.`, M + 0.18, 2.16, W - (M * 2) - 0.36, 0.22, { max: 330, fontSize: 8.8 });
      addStyledCard(final, M, 2.9, W - (M * 2), 2.25, { accent: green });
      addStyledLabel(final, 'Recommended validation steps', M + 0.18, 3.18, 2.7, green);
      addBullets(final, [
        'Review each rule against local workflow configuration, transition names and field names.',
        `Validate ${styleEngineName} validators, conditions, post-functions, listeners and scheduled rules in a sandbox project first.`,
        `Confirm workflow edit permissions, ${styleEngineName} app availability and automation user permissions.`,
        `Use the JSON/HTML export for copy-ready ${styleEngineName} expressions/scripts and detailed configuration.`,
        'Run a small pilot before enabling broadly, and monitor exceptions after release.',
      ], M + 0.18, 3.5, W - (M * 2) - 0.36, 1.0, { maxItems: 5, fontSize: 8.9, maxChars: 155 });
      if (packReqs.length) {
        addStyledCard(final, M, 5.45, W - (M * 2), 0.78, { accent: amber });
        addStyledLabel(final, 'Dependencies', M + 0.18, 5.72, 1.4, amber);
        addBullets(final, packReqs, M + 1.58, 5.63, W - (M * 2) - 1.75, 0.32, { maxItems: 4, fontSize: 8.0, maxChars: 120 });
      }
      addStyledFooter(final, page);
      return pptx;
    }

    let page = 1;
    const cover = pptx.addSlide();
    cover.background = { color: C.primaryDark };
    cover.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: W, h: H, fill: { color: C.primaryDark }, line: { color: C.primaryDark } });
    cover.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 0.12, h: H, fill: { color: C.accent }, line: { color: C.accent } });
    addMetricusLockup(cover, 0.55, 0.42);
    cover.addText(deckTitle, { x: 0.55, y: 1.2, w: 8.0, h: 0.55, fontSize: 28, bold: true, color: C.white, margin: 0, fit: 'shrink' });
    cover.addText(modelTitle || '', { x: 0.55, y: 1.86, w: 7.8, h: 0.28, fontSize: 12, color: 'D6E4EA', margin: 0, fit: 'shrink' });
    const coverText = engineKey === 'rovo'
      ? (packData?.rovo_readiness_rationale || deckSubtitle)
      : (packData?.source_context?.analysis_summary || packData?.summary || packData?.description || packData?.rules?.[0]?.rationale || deckSubtitle);
    cover.addText(trunc(coverText, 360), { x: 0.55, y: 2.34, w: 7.9, h: 1.02, fontSize: 11.2, color: 'EAF2F8', margin: 0.03, fit: 'shrink', valign: 'top' });
    cover.addShape(pptx.ShapeType.roundRect, { x: 9.2, y: 1.25, w: 2.6, h: 1.7, rectRadius: 0.1, fill: { color: C.white, transparency: 8 }, line: { color: C.white, transparency: 55 } });
    cover.addText(String(items.length), { x: 9.2, y: 1.55, w: 2.6, h: 0.45, fontSize: 34, bold: true, color: C.white, align: 'center', margin: 0 });
    cover.addText(itemLabel, { x: 9.2, y: 2.08, w: 2.6, h: 0.18, fontSize: 8.2, bold: true, color: 'D6E4EA', align: 'center', margin: 0 });
    if (packData?.rovo_readiness) cover.addText(`Rovo readiness: ${packData.rovo_readiness}`, { x: 9.2, y: 2.38, w: 2.6, h: 0.18, fontSize: 8.5, color: C.white, align: 'center', margin: 0 });
    cover.addText(`Prepared for Metricus · ${new Date().toLocaleDateString()}`, { x: 0.55, y: 6.9, w: 7.4, h: 0.18, fontSize: 8, color: 'B9C8D4', margin: 0 });

    const overview = pptx.addSlide(); page += 1;
    addHeader(overview, '1. Pack Overview', deckSubtitle);
    const intro = engineKey === 'rovo'
      ? (packData?.rovo_readiness_rationale || 'Generated Rovo agents and paired automation rules from process evidence.')
      : (packData?.source_context?.analysis_summary || packData?.summary || packData?.description || packData?.pack_summary || 'Generated automation rules and implementation artifacts from process evidence.');
    addCard(overview, M, 1.32, W - (M * 2), 0.82, { fill: C.tint, line: C.border, accent: C.primary });
    addLabel(overview, 'Summary', M + 0.18, 1.45, 1.2);
    addBody(overview, intro, M + 0.18, 1.65, W - (M * 2) - 0.36, 0.3, { max: 430, fontSize: 9.4 });

    const cols = 2;
    const cardW = (W - (M * 2) - 0.18) / cols;
    const startY = 2.34;
    const rowH = isJwtPpt ? 1.02 : 0.82;
    const overviewItems = isJwtPpt ? items.slice(0, 6) : items.slice(0, 8);
    overviewItems.forEach((r, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = M + col * (cardW + 0.18);
      const y = startY + row * rowH;
      const oCardH = isJwtPpt ? 0.88 : 0.68;
      addCard(overview, x, y, cardW, oCardH, { fill: C.white, line: C.border, accent: idx < 3 ? C.primary : C.accent });
      overview.addText(r.name || r.id, { x: x + 0.14, y: y + 0.1, w: cardW - 1.35, h: 0.2, fontSize: isJwtPpt ? jwtText.overviewTitle : 8.4, bold: true, color: C.navy, margin: 0, fit: 'shrink' });
      if (r.priority) addPill(overview, r.priority, x + cardW - 1.13, y + 0.08, 0.96, { fontSize: isJwtPpt ? 6.4 : 5.6, max: 20 });
      overview.addText((r.meta || []).slice(0, 3).join(' · ') || r.id, { x: x + 0.14, y: y + 0.34, w: cardW - 0.28, h: 0.14, fontSize: isJwtPpt ? jwtText.overviewMeta : 6.7, bold: true, color: C.primary, margin: 0, fit: 'shrink' });
      addBody(overview, r.purpose || r.evidence || r.trigger || '', x + 0.14, y + 0.52, cardW - 0.28, isJwtPpt ? 0.2 : 0.12, { max: isJwtPpt ? 150 : 125, fontSize: isJwtPpt ? jwtText.overviewBody : 6.6 });
    });
    if (packReqs.length) {
      const reqY = isJwtPpt ? 6.22 : 6.08;
      addCard(overview, M, reqY, W - (M * 2), 0.64, { fill: C.card, line: C.border, accent: C.warn });
      addLabel(overview, 'Technical Requirements', M + 0.18, reqY + 0.1, 1.8, C.warn);
      addBullets(overview, packReqs, M + 0.18, reqY + 0.28, W - (M * 2) - 0.36, 0.22, { maxItems: 3, fontSize: isJwtPpt ? 7.4 : 6.8, maxChars: 115 });
    }
    addFooter(overview, page);

    if (engineKey === 'rovo' && rovoPhases.length) {
      const seq = pptx.addSlide(); page += 1;
      addHeader(seq, '2. Implementation Sequence', 'Recommended rollout path for the Rovo pack');
      const phases = rovoPhases;
      const y = 1.42;
      const colW = (W - (M * 2) - 0.24) / 3;
      phases.slice(0, 3).forEach((p, idx) => {
        const x = M + idx * (colW + 0.12);
        addCard(seq, x, y, colW, 2.0, { fill: idx === 0 ? C.successBg : idx === 1 ? C.tint : C.warnBg, line: C.border, accent: idx === 0 ? C.success : idx === 1 ? C.primary : C.warn });
        seq.addText(String(idx + 1), { x: x + 0.18, y: y + 0.18, w: 0.42, h: 0.28, fontSize: 18, bold: true, color: idx === 0 ? C.success : idx === 1 ? C.primary : C.warn, margin: 0 });
        seq.addText(p.phase || p.name || `Phase ${idx + 1}`, { x: x + 0.68, y: y + 0.18, w: colW - 0.88, h: 0.28, fontSize: 10, bold: true, color: C.navy, margin: 0, fit: 'shrink' });
        addBody(seq, p.description || p.summary || p.objective || '', x + 0.18, y + 0.66, colW - 0.36, 1.02, { max: 260, fontSize: 8.6 });
      });
      addCard(seq, M, 4.04, W - (M * 2), 1.54, { fill: C.white, line: C.border, accent: C.primaryDark });
      addLabel(seq, 'Rollout implication', M + 0.18, 4.2, 1.6, C.primaryDark);
      addBody(seq, 'Start with low-build knowledge and search capabilities, then deploy standard Rovo Agents where repeatable process delays and rework loops are already evident. Keep Rovo Studio or deeper customisation for later where the evidence shows multi-step orchestration is needed.', M + 0.18, 4.48, W - (M * 2) - 0.36, 0.54, { max: 420, fontSize: 9.2 });
      addFooter(seq, page);
    }

    const detailStartNumber = engineKey === 'rovo' && rovoPhases.length ? 3 : 2;
    const addDetailSlide = (r, idx) => {
      const s = pptx.addSlide(); page += 1;
      addHeader(s, `${detailStartNumber + idx}. ${r.name || r.id}`, (r.meta || []).join(' · '));

      if (['scriptrunner', 'jmwe', 'jwt'].includes(engineKey)) {
        const fullW = W - (M * 2);
        addCard(s, M, 1.28, fullW, 0.9, { fill: C.tint, line: C.border, accent: C.primary });
        addLabel(s, 'Rule intent', M + 0.18, 1.42, 1.5, C.primary);
        addBody(s, r.purpose || r.evidence || '', M + 0.18, 1.64, fullW - 0.36, 0.3, { max: 310, fontSize: 9.6 });

        const leftW = 5.55;
        const rightX = M + leftW + 0.28;
        const rightW = W - M - rightX;
        const rowY = 2.42;
        addCard(s, M, rowY, leftW, 2.18, { fill: C.white, line: C.border, accent: C.primaryDark });
        addLabel(s, 'Trigger', M + 0.18, rowY + 0.14, 1.1, C.primaryDark);
        addBody(s, r.trigger || 'JWT workflow control', M + 1.26, rowY + 0.14, leftW - 1.46, 0.16, { max: 90, fontSize: 8.8, color: C.navy });
        let yy = rowY + 0.48;
        if (r.schedule) {
          addLabel(s, 'Schedule', M + 0.18, yy, 1.1, C.primaryDark);
          addBody(s, r.schedule, M + 1.26, yy, leftW - 1.46, 0.16, { max: 95, fontSize: 8.5, color: C.navy });
          yy += 0.34;
        }
        if (r.jql) {
          addLabel(s, 'JQL scope', M + 0.18, yy, 1.1, C.primaryDark);
          addBody(s, r.jql, M + 1.26, yy, leftW - 1.46, 0.3, { max: 145, fontSize: 8.1, color: C.navy });
          yy += 0.5;
        }
        addLabel(s, 'When', M + 0.18, yy, 1.1, C.primaryDark);
        addBullets(s, r.when || r.conditions || [], M + 1.26, yy, leftW - 1.46, 0.54, { maxItems: 3, fontSize: 8.0, maxChars: 105 });

        addCard(s, rightX, rowY, rightW, 2.18, { fill: C.white, line: C.border, accent: C.success });
        addLabel(s, 'Actions', rightX + 0.18, rowY + 0.14, 1.1, C.success);
        addBullets(s, r.actions || [], rightX + 0.18, rowY + 0.4, rightW - 0.36, 0.9, { maxItems: 5, fontSize: 8.2, maxChars: 108 });
        if ((r.conditions || []).length) {
          addLabel(s, 'Controls', rightX + 0.18, rowY + 1.42, 1.2, C.warn);
          addBullets(s, r.conditions || [], rightX + 0.18, rowY + 1.66, rightW - 0.36, 0.34, { maxItems: 2, fontSize: 7.6, maxChars: 105 });
        }

        addCard(s, M, 4.9, leftW, 1.24, { fill: C.white, line: C.border, accent: C.accent });
        addLabel(s, 'Expected benefit', M + 0.18, 5.06, 1.7, C.accent);
        addBody(s, r.benefit || r.evidence || 'Evidence-backed automation opportunity generated from process analysis.', M + 0.18, 5.32, leftW - 0.36, 0.34, { max: 250, fontSize: 8.4 });
        if (r.evidence) addBody(s, r.evidence, M + 0.18, 5.72, leftW - 0.36, 0.2, { max: 190, fontSize: 7.4, color: C.muted });

        addCard(s, rightX, 4.9, rightW, 1.24, { fill: C.card, line: C.border, accent: C.primary });
        addLabel(s, 'JWT configuration logic', rightX + 0.18, 5.06, 2.1, C.primary);
        addBody(s, r.scriptPseudo || 'Configure the JWT validator, condition, post-function or scheduled rule described above. Full copy-ready details are in the JSON export.', rightX + 0.18, 5.32, rightW - 0.36, 0.34, { max: 250, fontSize: 8.1 });
        addBody(s, 'Full JWT configuration remains in the JSON export.', rightX + 0.18, 5.78, rightW - 0.36, 0.14, { max: 100, fontSize: 7.1, color: C.muted });
        addFooter(s, page);
        return;
      }

      const topY = 1.32;
      const purposeH = isJwtPpt ? 0.96 : 0.82;
      addCard(s, M, topY, W - (M * 2), purposeH, { fill: C.tint, line: C.border, accent: C.primary });
      addLabel(s, engineKey === 'rovo' ? 'Purpose' : 'Purpose / rule intent', M + 0.18, topY + 0.13, 2.0);
      addBody(s, r.purpose || r.evidence || '', M + 0.18, topY + 0.34, W - (M * 2) - 0.36, isJwtPpt ? 0.46 : 0.32, { max: isJwtPpt ? 380 : 430, fontSize: isJwtPpt ? jwtText.detailBody : 9.0 });

      const y0 = isJwtPpt ? 2.48 : 2.34;
      const boxH = isJwtPpt ? 1.36 : 1.28;
      const w3 = (W - (M * 2) - 0.24) / 3;
      const panels = [
        ['Trigger', r.trigger || 'Not specified', C.primary],
        [engineKey === 'rovo' ? 'Prerequisites' : 'Conditions', r.conditions?.length ? r.conditions : (r.requirements || []), C.warn],
        ['Actions', r.actions, C.success],
      ];
      panels.forEach(([label, val, color], i) => {
        const x = M + i * (w3 + 0.12);
        addCard(s, x, y0, w3, boxH, { fill: C.white, line: C.border, accent: color });
        addLabel(s, label, x + 0.14, y0 + 0.14, w3 - 0.28, color);
        if (Array.isArray(val)) addBullets(s, val, x + 0.14, y0 + 0.38, w3 - 0.28, isJwtPpt ? 0.76 : 0.68, { maxItems: isJwtPpt ? 4 : 5, fontSize: isJwtPpt ? jwtText.detailBullet : 7.4, maxChars: isJwtPpt ? 86 : 92 });
        else addBody(s, val, x + 0.14, y0 + 0.38, w3 - 0.28, isJwtPpt ? 0.72 : 0.62, { max: isJwtPpt ? 150 : 180, fontSize: isJwtPpt ? jwtText.detailSmall : 7.6 });
      });

      const y1 = isJwtPpt ? 4.02 : 3.86;
      const evidenceTitle = (engineKey === 'scriptrunner' || engineKey === 'jmwe') ? 'Evidence / success criteria' : 'Expected benefit / evidence';
      addCard(s, M, y1, 6.05, 1.28, { fill: C.white, line: C.border, accent: C.accent });
      addLabel(s, evidenceTitle, M + 0.16, y1 + 0.14, 2.5, C.accent);
      const evidenceLines = (engineKey === 'scriptrunner' || engineKey === 'jmwe')
        ? list(r.benefit || r.evidence)
        : [r.benefit || r.evidence || 'Evidence-backed automation opportunity generated from process analysis.'];
      if (evidenceLines.length > 1) addBullets(s, evidenceLines, M + 0.16, y1 + 0.38, 5.72, 0.58, { maxItems: 3, fontSize: isJwtPpt ? jwtText.detailBullet : 7.4, maxChars: isJwtPpt ? 115 : 130 });
      else addBody(s, evidenceLines[0] || 'Evidence-backed automation opportunity generated from process analysis.', M + 0.16, y1 + 0.38, 5.72, 0.4, { max: isJwtPpt ? 260 : 310, fontSize: isJwtPpt ? jwtText.detailSmall : 7.8 });
      if ((engineKey === 'scriptrunner' || engineKey === 'jmwe') && r.jql) addBody(s, `JQL scope: ${r.jql}`, M + 0.16, y1 + 0.9, 5.72, 0.16, { max: 170, fontSize: 6.8, color: C.muted });
      else if (r.evidence && !(engineKey === 'scriptrunner' || engineKey === 'jmwe')) addBody(s, r.evidence, M + 0.16, y1 + 0.78, 5.72, 0.28, { max: 220, fontSize: 7.0, color: C.muted });

      addCard(s, 6.72, y1, W - M - 6.72, 1.28, { fill: C.white, line: C.border, accent: C.primaryDark });
      addLabel(s, (engineKey === 'scriptrunner' || engineKey === 'jmwe') ? 'Build notes' : 'Implementation notes', 6.88, y1 + 0.14, 1.7, C.primaryDark);
      const notes = (engineKey === 'scriptrunner' || engineKey === 'jmwe') ? [
        r.scriptPseudo ? `Pseudo logic: ${r.scriptPseudo}` : '',
        r.instructions ? 'Detailed configuration appears on the implementation slide.' : '',
        'Use the JSON/HTML export for the full copy-ready artifact.',
      ].filter(Boolean) : engineKey === 'jwt' ? [
        r.scriptPseudo ? `JWT logic: ${r.scriptPseudo}` : '',
        'Use the JSON/HTML export for the full copy-ready JWT configuration.',
      ].filter(Boolean) : [
        r.pairing ? `Automation pairing: ${r.pairing}` : '',
        r.instructions ? `Instructions available on implementation slide.` : '',
        r.exportObj ? `${engineKey === 'rovo' ? 'Paired Jira Automation' : 'Importable Jira Automation'} JSON included in export.` : '',
      ].filter(Boolean);
      addBullets(s, notes.length ? notes : ['Use the generated implementation artifact from the JSON export for import/configuration.'], 6.88, y1 + 0.38, W - M - 7.04, 0.7, { maxItems: isJwtPpt ? 3 : 4, fontSize: isJwtPpt ? jwtText.detailBullet : 7.3, maxChars: isJwtPpt ? 100 : 115 });
      addFooter(s, page);
    };

    const addImplementationSlide = (r, idx) => {
      // JWT intentionally uses the same one-slide-per-rule structure as
      // ScriptRunner and JMWE. Do not create separate JWT implementation
      // artifact slides; those made the JWT export look different and too dense.
      if (engineKey === 'jwt') return;
      const s = pptx.addSlide(); page += 1;
      addHeader(s, `${detailStartNumber + items.length + idx}. Implementation Artifact — ${r.name || r.id}`, 'Configuration details for build/import validation');

      if (engineKey === 'jwt') {
        addCard(s, M, 1.32, W - (M * 2), 0.68, { fill: C.tint, line: C.border, accent: C.primary });
        addLabel(s, 'Implementation type', M + 0.18, 1.45, 2.2, C.primary);
        addBody(s, 'JWT workflow configuration · validators, conditions, post-functions, scheduled rules and expression snippets', M + 0.18, 1.66, W - (M * 2) - 0.36, 0.2, { max: 210, fontSize: jwtText.implBody });

        addCard(s, M, 2.22, 5.9, 2.02, { fill: C.white, line: C.border, accent: C.primaryDark });
        addLabel(s, 'Configuration summary', M + 0.18, 2.38, 2.2, C.primaryDark);
        addKeyValueRows(s, (r.implementationRows || []).slice(0, 5), M + 0.18, 2.65, 5.54, 1.16, { fontSize: jwtText.implRow, labelSize: jwtText.implLabel, labelW: 1.25, max: 118 });

        addCard(s, 6.58, 2.22, W - M - 6.58, 2.02, { fill: C.white, line: C.border, accent: C.success });
        addLabel(s, 'JWT transition / schedule configuration', 6.76, 2.38, 3.0, C.success);
        const jwtConfigLines = (r.actionConfigRows || []).map(([name, cfg]) => `${name}: ${cfg}`);
        addBullets(s, jwtConfigLines.length ? jwtConfigLines : list(r.actions), 6.76, 2.66, W - M - 6.94, 1.08, { maxItems: 4, fontSize: jwtText.implBullet, maxChars: 105 });

        addCard(s, M, 4.58, 5.9, 1.36, { fill: C.card, line: C.border, accent: C.warn });
        addLabel(s, 'Preconditions / rollout controls', M + 0.18, 4.74, 2.5, C.warn);
        addBullets(s, [...list(r.conditions), ...list(r.requirements)].filter(Boolean), M + 0.18, 5.0, 5.54, 0.52, { maxItems: 3, fontSize: jwtText.implBullet, maxChars: 105 });

        addCard(s, 6.58, 4.58, W - M - 6.58, 1.36, { fill: C.card, line: C.border, accent: C.accent });
        addLabel(s, 'Success criteria / expressions', 6.76, 4.74, 2.6, C.accent);
        const exprLines = (r.expressionRows || []).map(([purpose, expr]) => `${purpose}: ${expr}`);
        addBullets(s, [...list(r.validationPoints), ...exprLines].filter(Boolean), 6.76, 5.0, W - M - 6.94, 0.52, { maxItems: 3, fontSize: jwtText.implBullet, maxChars: 105 });

        addCard(s, M, 6.18, W - (M * 2), 0.42, { fill: C.darkCard, line: C.darkCard, accent: C.primary });
        addBody(s, 'Full copy-ready JWT expressions and configuration steps remain in the HTML and JSON exports. The PPT presents the implementation logic in readable form rather than squeezing code into tiny text.', M + 0.18, 6.3, W - (M * 2) - 0.36, 0.12, { max: 260, fontSize: jwtText.note, color: 'E8EEFF' });
        addFooter(s, page);
        return;
      }

      if (engineKey === 'scriptrunner' || engineKey === 'jmwe') {
        addCard(s, M, 1.32, W - (M * 2), 0.68, { fill: C.tint, line: C.border, accent: C.primary });
        addLabel(s, engineKey === 'scriptrunner' ? 'Implementation type' : 'Workflow extension type', M + 0.18, 1.45, 2.2, C.primary);
        addBody(s, engineKey === 'scriptrunner' ? `ScriptRunner Cloud rule · ${r.scriptLang || 'groovy'}` : 'JMWE workflow configuration · validators, post-functions and transition controls', M + 0.18, 1.66, W - (M * 2) - 0.36, 0.18, { max: 180, fontSize: 8.6 });

        addCard(s, M, 2.22, 5.9, 2.02, { fill: C.white, line: C.border, accent: C.primaryDark });
        addLabel(s, 'Configuration summary', M + 0.18, 2.38, 2.2, C.primaryDark);
        addKeyValueRows(s, (r.implementationRows || []).slice(0, 5), M + 0.18, 2.65, 5.54, 1.16, { fontSize: jwtText.implRow, labelSize: jwtText.implLabel, labelW: 1.25, max: 118 });

        addCard(s, 6.58, 2.22, W - M - 6.58, 2.02, { fill: C.white, line: C.border, accent: C.success });
        addLabel(s, engineKey === 'scriptrunner' ? 'Script / action logic' : 'JMWE action configuration', 6.76, 2.38, 2.4, C.success);
        const actionLines = engineKey === 'jmwe'
          ? (r.actionConfigRows || []).map(([name, cfg]) => `${name}: ${cfg}`)
          : [r.scriptPseudo || '', ...list(r.actions).map(a => `Action: ${a}`)].filter(Boolean);
        addBullets(s, actionLines.length ? actionLines : list(r.actions), 6.76, 2.66, W - M - 6.94, 1.08, { maxItems: 5, fontSize: 7.1, maxChars: 135 });

        addCard(s, M, 4.58, 5.9, 1.36, { fill: C.card, line: C.border, accent: C.warn });
        addLabel(s, 'Validation / controls', M + 0.18, 4.74, 2.0, C.warn);
        addBullets(s, [...list(r.conditions), ...list(r.requirements)].filter(Boolean), M + 0.18, 5.0, 5.54, 0.52, { maxItems: 3, fontSize: jwtText.implBullet, maxChars: 105 });

        addCard(s, 6.58, 4.58, W - M - 6.58, 1.36, { fill: C.card, line: C.border, accent: C.accent });
        addLabel(s, 'Success criteria / telemetry', 6.76, 4.74, 2.4, C.accent);
        addBullets(s, r.validationPoints || [], 6.76, 5.0, W - M - 6.94, 0.52, { maxItems: 4, fontSize: 7.1, maxChars: 125 });

        addCard(s, M, 6.18, W - (M * 2), 0.42, { fill: C.darkCard, line: C.darkCard, accent: C.primary });
        addBody(s, 'Full copy-ready scripts / JMWE JSON remain in the HTML and JSON exports. The PPT intentionally presents the implementation logic in readable form rather than squeezing code into tiny text.', M + 0.18, 6.31, W - (M * 2) - 0.36, 0.1, { max: 280, fontSize: 6.6, color: 'E8EEFF' });
        addFooter(s, page);
        return;
      }

      const hasJson = !!r.exportObj;
      const leftW = hasJson ? 6.2 : W - (M * 2);
      addCard(s, M, 1.32, leftW, 2.05, { fill: C.white, line: C.border, accent: C.primary });
      addLabel(s, engineKey === 'rovo' ? 'Agent instructions' : 'Automation configuration', M + 0.18, 1.48, 2.2, C.primary);
      const instructionText = engineKey === 'rovo'
        ? (r.instructions || 'No agent instructions required for this item; use the Rovo product capability and paired automation where applicable.')
        : [r.trigger ? `Trigger: ${r.trigger}` : '', ...(r.actions || []).map(a => `Action: ${a}`)].filter(Boolean).join('\n');
      addBullets(s, String(instructionText).split(/\n|\.\s+(?=\d\.|[A-Z])/).filter(Boolean), M + 0.18, 1.78, leftW - 0.36, 1.18, { maxItems: 7, fontSize: 7.8, maxChars: 150 });

      if (r.pairing) {
        addCard(s, M, 3.62, leftW, 1.08, { fill: C.tint, line: C.border, accent: C.accent });
        addLabel(s, 'Jira Automation pairing', M + 0.18, 3.78, 2.1, C.accent);
        addBody(s, r.pairing, M + 0.18, 4.04, leftW - 0.36, 0.38, { max: 300, fontSize: 8.2 });
      }

      const prereqY = r.pairing ? 4.98 : 3.62;
      if ((r.conditions || r.requirements || []).length) {
        addCard(s, M, prereqY, leftW, 0.84, { fill: C.card, line: C.border, accent: C.warn });
        addLabel(s, 'Prerequisites / validation', M + 0.18, prereqY + 0.14, 2.2, C.warn);
        addBullets(s, r.conditions || r.requirements || [], M + 0.18, prereqY + 0.36, leftW - 0.36, 0.25, { maxItems: 3, fontSize: 6.9, maxChars: 120 });
      }

      if (hasJson) {
        addCard(s, 6.86, 1.32, W - M - 6.86, 4.5, { fill: C.darkCard, line: C.darkCard, accent: C.primary });
        addLabel(s, engineKey === 'rovo' ? 'Paired Jira Automation JSON' : 'Importable Jira Automation JSON', 7.04, 1.5, 2.8, 'B9D6FF');
        addKeyValueRows(s, compactJsonRows(r.exportObj), 7.04, 1.82, W - M - 7.22, 2.12, { fontSize: 7.2, labelSize: 6.5, labelW: 1.12, max: 130, labelColor: 'B9C8D4' });
        addCard(s, 7.04, 4.22, W - M - 7.22, 1.12, { fill: '252C3A', line: '38445A' });
        addLabel(s, 'Export note', 7.2, 4.38, 1.1, 'B9D6FF');
        addBody(s, 'The full importable JSON remains available in the JSON export. This PPT shows the configuration-critical fields in a readable format so the deck does not rely on unreadably small code blocks.', 7.2, 4.62, W - M - 7.54, 0.38, { max: 260, fontSize: 7.4, color: 'E8EEFF' });
      }
      addFooter(s, page);
    };

    items.forEach((r, idx) => addDetailSlide(r, idx));
    items.forEach((r, idx) => {
      const needsImpl = engineKey !== 'jwt' && (
        engineKey === 'rovo'
          ? (r.instructions || r.pairing || r.exportObj)
          : (r.exportObj || r.trigger || (r.actions || []).length || r.instructions || (r.actionConfigRows || []).length || (r.expressionRows || []).length)
      );
      if (needsImpl) addImplementationSlide(r, idx);
    });

    const final = pptx.addSlide(); page += 1;
    addHeader(final, `${page}. Implementation Checklist`, 'Review, validate and import/configure the generated automation pack');
    const checklist = [
      'Review each generated rule/agent against local workflow configuration and field names.',
      'Validate triggers, conditions and actions in a sandbox or development project first.',
      'Confirm permissions, app availability and required third-party marketplace apps.',
      'Use the JSON export for direct rule import where supported.',
      'Run a small pilot before enabling automation broadly and monitor exceptions after release.',
    ];
    addCard(final, M, 1.36, W - (M * 2), 1.08, { fill: C.tint, line: C.border, accent: C.primary });
    addLabel(final, 'Implementation note', M + 0.18, 1.52, 1.5);
    addBody(final, `This deck summarises the generated ${packTitle || 'automation pack'} content in a client-readable format. It intentionally avoids unreadably small full-code blocks; use the JSON export for copy/import artifacts.`, M + 0.18, 1.74, W - (M * 2) - 0.36, 0.34, { max: 360, fontSize: 9 });
    addCard(final, M, 2.7, W - (M * 2), 2.18, { fill: C.white, line: C.border, accent: C.success });
    addLabel(final, 'Recommended validation steps', M + 0.18, 2.9, 2.1, C.success);
    addBullets(final, checklist, M + 0.18, 3.22, W - (M * 2) - 0.36, 1.08, { maxItems: 5, fontSize: 9.1, maxChars: 155 });
    if (packReqs.length) {
      addCard(final, M, 5.18, W - (M * 2), 0.94, { fill: C.card, line: C.border, accent: C.warn });
      addLabel(final, 'Dependencies', M + 0.18, 5.34, 1.4, C.warn);
      addBullets(final, packReqs, M + 0.18, 5.58, W - (M * 2) - 0.36, 0.3, { maxItems: 4, fontSize: 7.1, maxChars: 130 });
    }
    addFooter(final, page);
    return pptx;
  };


  const handleExportPPTX = async (themeId) => {
    const packData = getActiveExportData();
    const packTitle = getExportTitle();
    if (!packData) return;
    try {
      const slug = (packTitle || 'automation-pack').replace(/\s+/g, '-').toLowerCase();
      const modelSlug = (modelTitle || 'report').replace(/\s+/g, '-').toLowerCase();
      const platform = isServiceNowPackKey(exportType) ? 'servicenow' : 'jira';
      const selectedTheme = themeId || themeFromPlatform(platform) || DEFAULT_REPORT_THEME;
      const pptx = buildAutomationPackPptxDedicated(packData, packTitle, exportType, selectedTheme, { platform, modelTitle });
      await pptx.writeFile({ fileName: `${slug}-${modelSlug}.pptx` });
      setIsExportModalOpen(false);
    } catch (err) {
      console.error('Automation pack PPT export failed:', err);
      alert(`Failed to export PowerPoint. ${err?.message || 'Please try again.'}`);
    }
  };

  const downloadBlob = (content, mimeType, fileName) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportHTML = () => {
    const packData = getActiveExportData();
    const packTitle = getExportTitle();
    if (!packData) return;
    const slug = (packTitle || 'automation-pack').replace(/\s+/g, '-').toLowerCase();
    const html = exportType === 'rovo'
      ? buildRovoPackExportHTML(packData, modelTitle)
      : buildAutomationPackExportHTML(packData, packTitle);
    downloadBlob(html, 'text/html', `${slug}-${(modelTitle || 'report').replace(/\s+/g, '-').toLowerCase()}.html`);
    setIsExportModalOpen(false);
  };

  const handleExportPDF = () => {
    const packData = getActiveExportData();
    const packTitle = getExportTitle();
    if (!packData) return;
    const html = exportType === 'rovo'
      ? buildRovoPackExportHTML(packData, modelTitle)
      : buildAutomationPackExportHTML(packData, packTitle);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:none;left:-9999px;top:-9999px;';
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
    setIsExportModalOpen(false);
  };

  const handleExportJSON = () => {
    const packData = getActiveExportData();
    const packTitle = getExportTitle();
    if (!packData) return;
    const slug = (packTitle || 'automation-pack').replace(/\s+/g, '-').toLowerCase();
    downloadBlob(JSON.stringify(packData, null, 2), 'application/json', `${slug}-${(modelTitle || 'report').replace(/\s+/g, '-').toLowerCase()}.json`);
    setIsExportModalOpen(false);
  };

  const handleSaveSnapshot = async (description) => {
    if (activeView === 'grid') return;
    const packData = DATA_MAP[activeView] || null;
    if (!packData) return;
    setSnapshotSaving(true);
    try {
      const packTitle = allEngines.find(e => e.key === activeView)?.label || 'Automation Pack';
      const html = activeView === 'rovo'
        ? buildRovoPackExportHTML(packData, modelTitle)
        : buildAutomationPackExportHTML(packData, packTitle);
      await saveSnapshot({
        modelId,
        tab: 'automationPacks',
        tabLabel: packTitle,
        description,
        html,
        AppVersion: getAppVersion(),
      });
    } finally {
      setSnapshotSaving(false);
      setSnapshotModalOpen(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const EngineCard = ({ btn, featured }) => (
    <button
      onClick={() => btn.generated ? setActiveView(btn.key) : btn.generate()}
      style={{
        display: 'flex',
        flexDirection: featured ? 'row' : 'column',
        alignItems: featured ? 'center' : 'flex-start',
        gap: featured ? 14 : 12,
        padding: featured ? '22px 24px' : '20px 18px',
        borderRadius: 8, cursor: 'pointer',
        textAlign: featured ? 'left' : 'left',
        width: '100%', height: '100%',
        background: btn.generated ? `${btn.accent}08` : '#fff',
        border: `2px solid ${btn.generated ? btn.accent : '#dfe1e6'}`,
        borderLeft: featured ? `4px solid ${btn.accent}` : `2px solid ${btn.generated ? btn.accent : '#dfe1e6'}`,
        borderTop: !featured ? `3px solid ${btn.accent}` : undefined,
        transition: 'all 0.15s',
        boxShadow: btn.generated ? `0 2px 8px ${btn.accent}22` : '0 1px 2px rgba(9,30,66,0.06)',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = btn.accent; e.currentTarget.style.boxShadow = `0 2px 8px ${btn.accent}22`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = btn.generated ? btn.accent : '#dfe1e6'; e.currentTarget.style.boxShadow = btn.generated ? `0 2px 8px ${btn.accent}22` : '0 1px 2px rgba(9,30,66,0.06)'; }}
    >
      {/* Icon + label row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: featured ? 1 : undefined, minWidth: 0, width: !featured ? '100%' : undefined }}>
        {btn.icon ? <img src={btn.icon} alt={btn.label} style={{ width: featured ? 52 : 48, height: featured ? 52 : 48, objectFit: 'contain', flexShrink: 0 }} /> : <span style={{ width: featured ? 52 : 48, height: featured ? 52 : 48, borderRadius: 12, background: btn.accent, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Package size={featured ? 26 : 24} color="white" /></span>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: featured ? 17 : 16, fontWeight: 700, color: btn.generated ? btn.accent : '#172b4d', lineHeight: 1.2 }}>{btn.label}</div>
          {btn.sublabel && <div style={{ fontSize: 13, color: '#6b778c', marginTop: 4 }}>{btn.sublabel}</div>}
          {btn.generated && <div style={{ fontSize: 10, color: '#00875A', fontWeight: 700, marginTop: 2 }}>✓ GENERATED</div>}
        </div>
        {/* Featured: button inline */}
        {featured && (
          <div style={{
            fontSize: 12, fontWeight: 700, padding: '7px 16px', borderRadius: 5, flexShrink: 0,
            background: btn.generated ? '#E3FCEF' : btn.accent,
            color: btn.generated ? '#006644' : '#fff',
            border: btn.generated ? '1px solid #57D9A3' : 'none',
          }}>
            {btn.generated ? 'View →' : 'Generate →'}
          </div>
        )}
      </div>
      {/* Specialist: button below */}
      {!featured && (
        <div style={{
          fontSize: 12, fontWeight: 700, padding: '8px 18px', borderRadius: 5,
          width: 132, maxWidth: '100%', textAlign: 'center', alignSelf: 'center',
          background: btn.generated ? '#E3FCEF' : btn.accent,
          color: btn.generated ? '#006644' : '#fff',
          border: btn.generated ? '1px solid #57D9A3' : 'none',
          marginTop: 'auto',
        }}>
          {btn.generated ? 'View →' : 'Generate →'}
        </div>
      )}
    </button>
  );

  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ── Header bar ── */}
      <ConsultantTabHeader
        title={activeView === 'grid' ? 'AI & Automation Packs' : activeLabel}
        subtitle={activeView === 'grid'
          ? 'Generate implementation-ready rules, agents, and blueprints from your process evidence'
          : 'Review rules, copy implementation artifacts, or export the full pack'}
        icon={activeView === 'grid' || !activeIcon
          ? <Package size={20} color="white" />
          : <img src={activeIcon} alt={activeLabel} style={{ width: 24, height: 24, objectFit: 'contain' }} />}
        iconBackground={activeView === 'grid'
          ? 'linear-gradient(135deg, #0052CC 0%, #6554C0 100%)'
          : `linear-gradient(135deg, ${activeAccent} 0%, ${activeAccent}99 100%)`}
        titleColor={activeView === 'grid' ? '#0052CC' : activeAccent}
        actions={(
          <>
{aiScope.controls}
            {activeView !== 'grid' && (
              <>
                <button style={getHeaderSecondaryButtonStyle()} onClick={() => setActiveView('grid')}>← Back</button>
                <SaveSnapshotButton color={activeAccent} onClick={() => setSnapshotModalOpen(true)} />
                <button style={getHeaderPrimaryButtonStyle(activeAccent)} onClick={handleResetPack}>New Pack</button>
                <button style={getHeaderSecondaryButtonStyle(activeAccent)} onClick={() => { setExportType(activeView); setIsExportModalOpen(true); }}>Export...</button>
              </>
            )}
          </>
        )}
      />
      {aiScope.panels}

      {/* ── Grid view ── */}
      {activeView === 'grid' && (
        <div style={{ background: '#fff', padding: '24px 28px 28px', margin: '0 10px 10px', borderRadius: '0 0 8px 8px', border: '2px solid #dfe1e6', borderTop: 'none' }}>

          <div style={{ maxWidth: isServiceNow ? 980 : 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 36 }}>

            {/* ── {isServiceNow ? 'ServiceNow Native Packs' : 'Atlassian Native Packs'} ── */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 4, height: 18, borderRadius: 2, background: '#0052CC' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0052CC', textTransform: 'uppercase', letterSpacing: '.06em' }}>{isServiceNow ? 'ServiceNow Native Packs' : 'Atlassian Native Packs'}</span>
                <span style={{ fontSize: 12, color: '#97a0af', marginLeft: 4 }}>{isServiceNow ? '— Flow Designer, routing, SLA, AI and self-service' : '— directly importable, no third-party apps'}</span>
              </div>
              <div style={{ display: isServiceNow ? 'grid' : 'flex', gridTemplateColumns: isServiceNow ? 'repeat(2, minmax(0, 1fr))' : undefined, flexDirection: isServiceNow ? undefined : 'column', gap: 10 }}>
                {featuredEngines.map(btn => <EngineCard key={btn.key} btn={btn} featured={!isServiceNow} />)}
              </div>
            </div>

            {/* ── Specialist Workflow Packs ── */}
            {!isServiceNow && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 4, height: 18, borderRadius: 2, background: '#0052CC' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0052CC', textTransform: 'uppercase', letterSpacing: '.06em' }}>Specialist Workflow Packs</span>
                <span style={{ fontSize: 12, color: '#97a0af', marginLeft: 4 }}>— requires third-party Jira apps</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {specialistEngines.map(btn => <EngineCard key={btn.key} btn={btn} featured={false} />)}
              </div>
            </div>
            )}

          </div>
        </div>
      )}

      {/* ── Pack views (all use PackDashboard) ── */}
      {activeView === 'jira'         && jiraGenerated         && <PackDashboard data={jiraData}         engine="jira"         crossPackThemes={crossPackFixThemes} />}
      {isServiceNowPackKey(activeView) && serviceNowGenerated[activeView] && <PackDashboard data={serviceNowPackData[activeView]} engine="servicenow" />}
      {activeView === 'rovo'         && rovoGenerated         && <PackDashboard data={rovoData}         engine="rovo"         />}
      {activeView === 'scriptrunner' && scriptRunnerGenerated && <PackDashboard data={scriptRunnerData} engine="scriptrunner" crossPackThemes={crossPackFixThemes} />}
      {activeView === 'jmwe'         && JMWEGenerated         && <PackDashboard data={JMWE}             engine="jmwe"         crossPackThemes={crossPackFixThemes} />}
      {activeView === 'jwt'          && JWTGenerated          && <PackDashboard data={JWT}              engine="jwt"          crossPackThemes={crossPackFixThemes} />}

      </div>


      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        isSaving={snapshotSaving}
        title="Save Automation Snapshot"
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title={`Export ${getExportTitle()}`}
        onExportHTML={handleExportHTML}
        onExportPDF={handleExportPDF}
        onExportJSON={handleExportJSON}
        onExportPPTX={handleExportPPTX}
      />
    </div>
  );
};

export default AutomationPacks;